if (!customElements.get('quick-add-modal')) {
  class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });

        subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          if (!this.hasAttribute('open')) return;

          const productInfo = this.modalContent?.querySelector('product-info');
          if (!productInfo || !event.data?.variant) return;

          if (
            this.classList.contains('quick-add--wheel') &&
            this.modalContent?.querySelector('.quick-add-wheel-options')
          ) {
            return;
          }

          const modalSectionId = productInfo.dataset.section;
          if (event.data.sectionId !== modalSectionId) return;

          const productVariant =
            this.quickAddProduct?.variants?.find(
              (v) => Number(v.id) === Number(event.data.variant.id)
            ) || event.data.variant;

          this.updateVariantMedia(productVariant, event.data.html);
          this.updateAvailabilityBar(productVariant);
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.classList.remove('quick-add--tire');
        this.classList.remove('quick-add--wheel');
        this.classList.remove('quick-add--suspension');
        this.modalContent.innerHTML = '';
        this.quickAddProduct = null;

        if (!preventFocus) this.clearUrlProductId();
        if (preventFocus) this.openedBy = null;
        this.closeImageLightbox();
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner')?.classList.remove('hidden');
        this.openerVendor = opener.getAttribute('data-product-vendor') || '';
        this.openerProductType = opener.getAttribute('data-product-type') || '';
        this.openerSku = opener.getAttribute('data-product-sku') || '';
        this.openerVehicles = opener.getAttribute('data-product-vehicles') || '';
        this.openerTireType = opener.getAttribute('data-product-tire-type') || '';
        this.openerTireSeason = opener.getAttribute('data-product-tire-season') || '';
        this.openerSpecialOrder = opener.getAttribute('data-product-special-order') === 'true';
        this.openerBackorder = opener.getAttribute('data-product-backorder') === 'true';

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then(async (responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) return;

            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);
            const liveProductInfo = this.modalContent.querySelector('product-info');
            const fetchedType = liveProductInfo?.getAttribute('data-product-type');
            if (fetchedType && !this.openerProductType) {
              this.openerProductType = fetchedType;
            }
            if (liveProductInfo?.getAttribute('data-product-special-order') === 'true') {
              this.openerSpecialOrder = true;
            }
            if (liveProductInfo?.getAttribute('data-product-backorder') === 'true') {
              this.openerBackorder = true;
            }
            this.injectManufacturerBelowImages(liveProductInfo || productElement);
            this.upsertStickyHeader(productElement);
            this.wrapPriceAndCta();
            const tireProductInfo = liveProductInfo || productElement;
            if (this.isWheelTireModalProduct(tireProductInfo)) {
              this.classList.add('quick-add--tire');
              if (this.isWheelProduct(tireProductInfo)) {
                this.classList.add('quick-add--wheel');
              } else {
                await this.setupTireOptions(tireProductInfo);
              }
            } else {
              this.injectProductInfoCard();
              this.injectAvailabilityBar();
              this.injectFreeShippingBar();
            }
            this.tidyInfoColumn(
              this.modalContent.querySelector('product-info')?.querySelector('.product__info-wrapper')
            );
            const liveAfterTidy = this.modalContent.querySelector('product-info');
            if (this.isWheelProduct(liveAfterTidy)) {
              await this.setupWheelOptions(liveAfterTidy);
            } else if (this.isTireProduct(liveAfterTidy)) {
              await this.setupTireOptions(liveAfterTidy);
            }
            await this.ensureNativeVariantPicker(liveAfterTidy || productElement);
            this.updateAvailabilityBar();
            await this.ensurePriceBreakdown(liveAfterTidy || productElement);
            await this.ensureThumbnailsStrip(liveAfterTidy || productElement);
            this.ensureImageLightbox(liveAfterTidy || productElement);
            this.ensureActiveMediaSlide();
            this.cancelScrollRevealAnimations(this.modalContent.querySelector('product-info') || this.modalContent);
            this.patchGuidePartUnavailableCta(opener);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
            this.syncUrlWithProductId(opener.getAttribute('data-product-id'));
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner')?.classList.add('hidden');
          });
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
        this.cancelScrollRevealAnimations(productElement);
      }

      cancelScrollRevealAnimations(productElement) {
        productElement
          .querySelectorAll('.scroll-trigger')
          .forEach((element) => element.classList.add('scroll-trigger--cancel'));
      }

      upsertStickyHeader(productElement) {
        const content = this.querySelector('.quick-add-modal__content');
        if (!content) return;

        const closeBtn = content.querySelector('[id^="ModalClose-"]');
        if (!closeBtn) return;

        const titleSource =
          productElement?.querySelector('.product__title a') ||
          productElement?.querySelector('.product__title') ||
          productElement?.querySelector('h1') ||
          null;
        const rawTitle = (titleSource?.textContent || '').trim();
        const isWheelTireModal = this.isWheelTireModalProduct(productElement);
        let vendor = (this.openerVendor || '').trim();
        if (!vendor && isWheelTireModal) {
          vendor = this.extractProductBrand(productElement) || '';
        }
        const title =
          isWheelTireModal && vendor
            ? QuickAddModal.combineVendorAndTitle(vendor, rawTitle)
            : rawTitle;
        const productType = isWheelTireModal
          ? 'Select your specifications'
          : (this.openerProductType || '').trim() || this.extractProductType(productElement);

        let header = content.querySelector('.quick-add-sticky-header');
        if (!header) {
          header = document.createElement('div');
          header.className = 'quick-add-sticky-header';
          header.innerHTML = `
            <div class="quick-add-sticky-header__title"></div>
            <div class="quick-add-sticky-header__actions"></div>
          `;
          content.insertBefore(header, content.firstChild);
        }

        let heading = header.querySelector('.quick-add-sticky-header__heading');
        if (!heading) {
          heading = document.createElement('div');
          heading.className = 'quick-add-sticky-header__heading';
          const legacyTitle = header.querySelector('.quick-add-sticky-header__title');
          if (legacyTitle) {
            heading.appendChild(legacyTitle);
          } else {
            heading.innerHTML = '<div class="quick-add-sticky-header__title"></div>';
          }
          header.insertBefore(heading, header.firstChild);
        }

        let titleEl = heading.querySelector('.quick-add-sticky-header__title');
        if (!titleEl) {
          titleEl = document.createElement('div');
          titleEl.className = 'quick-add-sticky-header__title';
          heading.prepend(titleEl);
        }
        heading.querySelector('.quick-add-sticky-header__vendor')?.remove();
        titleEl.textContent = title;

        let typeEl = heading.querySelector('.quick-add-sticky-header__type');
        if (productType) {
          if (!typeEl) {
            typeEl = document.createElement('div');
            typeEl.className = 'quick-add-sticky-header__type';
            heading.appendChild(typeEl);
          }
          typeEl.textContent = productType;
          typeEl.hidden = false;
        } else if (typeEl) {
          typeEl.textContent = '';
          typeEl.hidden = true;
        }

        let actionsEl = header.querySelector('.quick-add-sticky-header__actions');
        if (!actionsEl) {
          actionsEl = document.createElement('div');
          actionsEl.className = 'quick-add-sticky-header__actions';
          header.appendChild(actionsEl);
        }
        if (closeBtn.parentElement !== actionsEl) actionsEl.appendChild(closeBtn);
      }

      extractProductType(productElement) {
        if (!productElement) return '';

        try {
          const script = productElement.querySelector('script[type="application/ld+json"]');
          if (!script) return '';

          const data = JSON.parse(script.textContent);
          const product = Array.isArray(data)
            ? data.find((entry) => entry['@type'] === 'Product')
            : data['@type'] === 'Product'
              ? data
              : null;

          if (!product) return '';

          const category = product.category;
          if (typeof category === 'string') return category.trim();
          if (Array.isArray(category) && category.length) return String(category[0]).trim();
        } catch (error) {
          return '';
        }

        return '';
      }

      extractProductBrand(productElement) {
        if (!productElement) return '';

        try {
          const script = productElement.querySelector('script[type="application/ld+json"]');
          if (!script) return '';

          const data = JSON.parse(script.textContent);
          const product = Array.isArray(data)
            ? data.find((entry) => entry['@type'] === 'Product')
            : data['@type'] === 'Product'
              ? data
              : null;

          if (!product || !product.brand) return '';

          const brand = product.brand;
          if (typeof brand === 'string') return brand.trim();
          if (typeof brand === 'object' && brand !== null && typeof brand.name === 'string') {
            return brand.name.trim();
          }
        } catch (error) {
          return '';
        }

        return '';
      }

      injectProductInfoCard() {
        const root = this.modalContent;
        if (this.isWheelTireModalProduct(root?.querySelector('product-info'))) return;
        if (!root) return;

        const productInfo = root.querySelector('product-info');
        const infoWrapper = productInfo?.querySelector('.product__info-wrapper');
        if (!infoWrapper || infoWrapper.querySelector('.quick-add-product-info')) return;

        const brand = (this.openerVendor || '').trim();
        const category = (this.openerProductType || '').trim();
        let partNumber = (this.openerSku || '').trim();

        if (!partNumber) {
          const skuEl = root.querySelector('.product__sku:not(.visibility-hidden), [id^="Sku-"]');
          if (skuEl) {
            partNumber = skuEl.textContent.replace(/^SKU:?\s*/i, '').trim();
          }
        }

        const rows = [];
        if (brand) rows.push({ label: 'Brand', value: brand });
        if (category) rows.push({ label: 'Category', value: category });
        if (partNumber) rows.push({ label: 'Part Number', value: partNumber, key: 'sku' });
        if (!rows.length) return;

        const card = document.createElement('div');
        card.className = 'quick-add-product-info';
        card.innerHTML = `
          <h3 class="quick-add-product-info__title">Product Information</h3>
          <dl class="quick-add-product-info__list">
            ${rows
              .map(
                (row) => `
              <div class="quick-add-product-info__row"${row.key ? ` data-info-key="${row.key}"` : ''}>
                <dt>${row.label}:</dt>
                <dd>${row.value}</dd>
              </div>
            `
              )
              .join('')}
          </dl>
        `;

        const priceBox = infoWrapper.querySelector('.quick-add-price-cta-box');
        if (priceBox) {
          priceBox.insertAdjacentElement('afterend', card);
        } else {
          infoWrapper.appendChild(card);
        }
      }

      isSpecialOrderProduct(productElement) {
        if (this.openerSpecialOrder) return true;
        if (this.openedBy?.getAttribute('data-product-special-order') === 'true') return true;
        const info = productElement || this.modalContent?.querySelector('product-info');
        if (info?.getAttribute('data-product-special-order') === 'true') return true;
        return false;
      }

      isBackorderProduct(productElement) {
        if (this.openerBackorder) return true;
        if (this.openedBy?.getAttribute('data-product-backorder') === 'true') return true;
        const info = productElement || this.modalContent?.querySelector('product-info');
        if (info?.getAttribute('data-product-backorder') === 'true') return true;
        return false;
      }

      getSelectedModalVariant() {
        const variantId = this.modalContent?.querySelector(
          'input.product-variant-id, form[data-type="add-to-cart-form"] input[name="id"]'
        )?.value;
        if (!variantId || !this.quickAddProduct?.variants) return null;
        return (
          this.quickAddProduct.variants.find((variant) => Number(variant.id) === Number(variantId)) || null
        );
      }

      isCurrentVariantAvailable(variant) {
        if (variant && typeof variant.available === 'boolean') return Boolean(variant.available);

        const selected = this.getSelectedModalVariant();
        if (selected && typeof selected.available === 'boolean') return Boolean(selected.available);

        const root = this.modalContent;
        const price = root?.querySelector('.price');
        if (price?.classList.contains('price--sold-out')) return false;

        const variantInput = root?.querySelector(
          'input.product-variant-id, form[data-type="add-to-cart-form"] input[name="id"]'
        );
        if (variantInput?.disabled) return false;

        const submit = root?.querySelector('.product-form__submit, button[name="add"]');
        if (submit?.disabled || submit?.getAttribute('aria-disabled') === 'true') return false;

        return true;
      }

      getAvailabilityState(productElement, variant) {
        if (this.isSpecialOrderProduct(productElement)) {
          return {
            specialOrder: true,
            backorder: false,
            available: false,
            title: 'Special order item. Average lead time 2-4 months. No cancellations or returns.',
          };
        }

        if (this.isBackorderProduct(productElement)) {
          return {
            specialOrder: false,
            backorder: true,
            available: false,
            title: 'This item is currently out of stock. Available for backorder, contact for eta',
          };
        }

        const available = this.isCurrentVariantAvailable(variant);
        return {
          specialOrder: false,
          backorder: false,
          available,
          title: available
            ? 'In-stock! Typically ships in 3-5 business days.'
            : 'This item is currently out of stock.',
        };
      }

      static getAvailabilityBoxIconHtml() {
        return `
          <span class="quick-add-availability__icon" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" class="icon icon-box" viewBox="0 0 20 20" fill="currentColor" focusable="false">
              <path fill-rule="evenodd" d="M9.695.679a1 1 0 0 1 .674.003l7.846 2.859a1 1 0 0 1 .658.94v9.948a1 1 0 0 1-.553.894l-7.847 3.924a1 1 0 0 1-.882.006l-8.137-3.929a1 1 0 0 1-.565-.9v-9.94a1 1 0 0 1 .668-.943zM6.08 3.01 2.535 4.257l7.614 3.378 3.42-1.517zm.706-.248L14.2 5.838l3.337-1.48-7.51-2.736zM1.89 14.424v-9.36l7.76 3.444v9.662zm15.984.005-7.224 3.612V8.508l7.224-3.205z"/>
            </svg>
          </span>
        `;
      }

      injectAvailabilityBar() {
        const root = this.modalContent;
        if (this.isWheelTireModalProduct(root?.querySelector('product-info'))) return;
        if (!root) return;

        const productInfo = root.querySelector('product-info');
        const infoWrapper = productInfo?.querySelector('.product__info-wrapper');
        if (!infoWrapper) return;

        let bar = infoWrapper.querySelector('.quick-add-availability');
        if (!bar) {
          bar = document.createElement('div');
          bar.className = 'quick-add-availability';
          bar.setAttribute('role', 'status');
          bar.innerHTML = `
            ${QuickAddModal.getAvailabilityBoxIconHtml()}
            <p class="quick-add-availability__title"></p>
          `;

          const productInfoCard = infoWrapper.querySelector('.quick-add-product-info');
          const freeShipping = infoWrapper.querySelector('.quick-add-free-shipping');
          const priceBox = infoWrapper.querySelector('.quick-add-price-cta-box');
          if (productInfoCard) {
            productInfoCard.insertAdjacentElement('afterend', bar);
          } else if (freeShipping) {
            freeShipping.insertAdjacentElement('beforebegin', bar);
          } else if (priceBox) {
            priceBox.insertAdjacentElement('afterend', bar);
          } else {
            infoWrapper.appendChild(bar);
          }
        }

        this.updateAvailabilityBar();
      }

      updateAvailabilityBar(variant) {
        const root = this.modalContent;
        if (!root) return;
        if (this.isWheelTireModalProduct(root.querySelector('product-info'))) return;

        const productInfo = root.querySelector('product-info');
        const bar = root.querySelector('.quick-add-availability');
        if (!bar) return;

        const state = this.getAvailabilityState(productInfo, variant);
        bar.classList.toggle('quick-add-availability--special-order', state.specialOrder);
        bar.classList.toggle('quick-add-availability--backorder', state.backorder);
        bar.classList.toggle(
          'quick-add-availability--out-of-stock',
          !state.specialOrder && !state.backorder && !state.available
        );

        const title = bar.querySelector('.quick-add-availability__title');
        if (title) title.textContent = state.title;
      }

      injectFreeShippingBar() {
        const root = this.modalContent;
        if (this.isWheelTireModalProduct(root?.querySelector('product-info'))) return;
        if (!root) return;

        const productInfo = root.querySelector('product-info');
        const infoWrapper = productInfo?.querySelector('.product__info-wrapper');
        if (!infoWrapper || infoWrapper.querySelector('.quick-add-free-shipping')) return;

        const bar = document.createElement('div');
        bar.className = 'quick-add-free-shipping';
        bar.innerHTML = `
          <p class="quick-add-free-shipping__title">Free Shipping</p>
          <p class="quick-add-free-shipping__text">On orders over $100. Estimated delivery 3-5 business days.</p>
        `;

        const availability = infoWrapper.querySelector('.quick-add-availability');
        const productInfoCard = infoWrapper.querySelector('.quick-add-product-info');
        const priceBox = infoWrapper.querySelector('.quick-add-price-cta-box');
        if (availability) {
          availability.insertAdjacentElement('afterend', bar);
        } else if (productInfoCard) {
          productInfoCard.insertAdjacentElement('afterend', bar);
        } else if (priceBox) {
          priceBox.insertAdjacentElement('afterend', bar);
        } else {
          infoWrapper.appendChild(bar);
        }
      }

      wrapPriceAndCta() {
        const root = this.modalContent;
        if (!root) return;

        const productInfo = root.querySelector('product-info');
        if (!productInfo) return;

        const infoWrapper = productInfo.querySelector('.product__info-wrapper');
        if (!infoWrapper) return;

        const priceBlock = infoWrapper.querySelector('[id^="price-quickadd-"]');
        const buttons = infoWrapper.querySelector('product-form .product-form__buttons');
        if (!priceBlock || !buttons) return;

        if (infoWrapper.querySelector('.quick-add-price-cta-box')) return;

        const box = document.createElement('div');
        box.className = 'quick-add-price-cta-box';
        const quantityBlock = infoWrapper.querySelector('.product-form__quantity');

        // Insert box before price, then move price + quantity + buttons inside.
        priceBlock.parentElement.insertBefore(box, priceBlock);
        box.appendChild(priceBlock);

        if (quantityBlock) box.appendChild(quantityBlock);

        // product-form is useful for JS; keep it, but move its buttons into the box.
        const productForm = buttons.closest('product-form');
        if (productForm) box.appendChild(productForm);

        QuickAddModal.formatQuickAddPrices(box);
        QuickAddModal.enhanceAddToCartButton(box);
        QuickAddModal.observeQuickAddPriceUpdates(box, priceBlock);
        this.tidyInfoColumn(infoWrapper);
      }

      tidyInfoColumn(infoWrapper) {
        const infoContainer = infoWrapper?.querySelector('.product__info-container') || infoWrapper;
        if (!infoContainer) return;

        const priceBox = infoContainer.querySelector('.quick-add-price-cta-box');
        if (priceBox) {
          infoContainer.prepend(priceBox);
          // Tire/wheel selectors are inserted before the buy box; keep them above it
          // after prepend moves the price box to the top of the column.
          const tireOptions = infoContainer.querySelector('.quick-add-tire-options');
          if (tireOptions) infoContainer.insertBefore(tireOptions, priceBox);
        }

        infoContainer.querySelectorAll(':scope > div').forEach((node) => {
          if (
            node.classList.contains('quick-add-price-cta-box') ||
            node.classList.contains('quick-add-product-info') ||
            node.classList.contains('quick-add-availability') ||
            node.classList.contains('quick-add-free-shipping') ||
            node.classList.contains('quick-add-tire-options')
          ) {
            return;
          }

          if (!node.textContent.trim() && node.querySelector('product-form, variant-selects, [id^="price-"]') === null) {
            node.remove();
          }
        });
      }

      static stripTrailingPriceZeros(priceText) {
        const text = (priceText || '').trim();
        if (!text) return text;
        return text.replace(/([.,])00(?=\s|$|[)\]])/g, '');
      }

      static formatQuickAddPrices(container) {
        if (!container) return;

        container.querySelectorAll('.price-item--regular, .price-item--sale, .price-item').forEach((el) => {
          const formatted = QuickAddModal.stripTrailingPriceZeros(el.textContent);
          if (formatted !== el.textContent.trim()) el.textContent = formatted;
        });
      }

      static enhanceAddToCartButton(container) {
        const submit = container?.querySelector('.product-form__submit, button[name="add"]');
        if (!submit || submit.dataset.quickAddCartIcon === 'true') return;

        submit.dataset.quickAddCartIcon = 'true';
        submit.querySelector('.icon-wrap')?.remove();

        const buttonContent = submit.querySelector('.button__content') || submit;
        const label =
          submit.querySelector('.product-form__submit-label') ||
          buttonContent.querySelector('.product-form__submit-label');

        if (!label || submit.querySelector('.quick-add-cart-icon')) return;

        const iconWrap = document.createElement('span');
        iconWrap.className = 'quick-add-cart-icon svg-wrapper';
        iconWrap.setAttribute('aria-hidden', 'true');

        const iconNode = QuickAddModal.cloneQuickAddCartIcon();
        if (iconNode) iconWrap.appendChild(iconNode);

        if (buttonContent === submit) {
          submit.insertBefore(iconWrap, label);
        } else {
          buttonContent.insertBefore(iconWrap, label);
        }
      }

      static observeQuickAddPriceUpdates(box, priceBlock) {
        if (!box || !priceBlock || priceBlock.dataset.quickAddPriceObserved === 'true') return;

        priceBlock.dataset.quickAddPriceObserved = 'true';
        const observer = new MutationObserver(() => QuickAddModal.formatQuickAddPrices(box));
        observer.observe(priceBlock, { childList: true, subtree: true, characterData: true });
      }

      patchGuidePartUnavailableCta(opener) {
        if (!opener?.classList.contains('guide-driver-part-card__details')) return;

        const submit = this.modalContent.querySelector('.product-form__submit, button[name="add"]');
        if (!submit) return;

        const comingSoon =
          opener.getAttribute('data-product-coming-soon') === 'true' ||
          this.closest('.guide-driver-part-card-shell')?.getAttribute('data-product-coming-soon') === 'true';
        const label = submit.querySelector('.product-form__submit-label');

        if (comingSoon) {
          submit.disabled = true;
          const comingSoonText = window.variantStrings?.comingSoon || 'Coming soon';
          if (label) {
            label.textContent = comingSoonText;
          } else {
            submit.textContent = comingSoonText;
          }
          return;
        }

        if (!submit.disabled) return;

        const soldOutText = window.variantStrings?.soldOut || 'Sold out';
        if (label) {
          label.textContent = soldOutText;
        } else {
          submit.textContent = soldOutText;
        }
      }

      isGuidePartComingSoon() {
        if (this.openedBy?.getAttribute('data-product-coming-soon') === 'true') return true;
        return this.closest('.guide-driver-part-card-shell')?.getAttribute('data-product-coming-soon') === 'true';
      }

      updateGuidePartSubmitButton(submit, variant) {
        if (!submit) return;

        const label = submit.querySelector('.product-form__submit-label');
        const isGuidePartCard = Boolean(this.closest('.guide-driver-part-card-shell'));
        const comingSoon = isGuidePartCard && this.isGuidePartComingSoon();

        if (comingSoon) {
          submit.disabled = true;
          const comingSoonText = window.variantStrings?.comingSoon || 'Coming soon';
          if (label) label.textContent = comingSoonText;
          return;
        }

        submit.disabled = !variant.available;
        if (label && !variant.available) {
          label.textContent = window.variantStrings?.soldOut || 'Sold out';
        } else if (label) {
          label.textContent = window.variantStrings?.addToCart || 'Add to cart';
        }
      }

      ensureActiveMediaSlide() {
        const root = this.modalContent;
        if (!root) return;

        const slider = root.querySelector('[id^="Slider-Gallery-"]');
        if (!slider) return;

        const slides = Array.from(slider.querySelectorAll('li.product__media-item'));
        if (!slides.length) return;

        let active = slider.querySelector('.product__media-item.is-active');
        if (!active) {
          active = slides[0];
          active.classList.add('is-active');
        }

        slider.scrollLeft = 0;
      }

      static normalizeImageSrc(src) {
        if (!src) return '';
        const normalized = String(src).trim();
        if (normalized.startsWith('//')) return `https:${normalized}`;
        return normalized;
      }

      static imageDedupKey(src) {
        const normalized = QuickAddModal.normalizeImageSrc(src);
        if (!normalized) return '';

        try {
          const url = new URL(normalized);
          let pathname = decodeURIComponent(url.pathname).toLowerCase();

          const shopifyPathMatch = pathname.match(/(\/(?:files|products)\/.+)$/);
          if (shopifyPathMatch) pathname = shopifyPathMatch[1];

          pathname = pathname.replace(
            /_(\d+x\d*|\d+x|pico|icon|thumb|small|compact|medium|large|grande|original|master)(?=\.[a-z0-9]+$)/i,
            ''
          );

          return pathname;
        } catch {
          let fallback = decodeURIComponent(normalized.split('?')[0]).toLowerCase();
          fallback = fallback.replace(/_(\d+x\d*|\d+x)(?=\.[a-z0-9]+$)/i, '');
          const shopifyPathMatch = fallback.match(/(\/(?:files|products)\/.+)$/);
          if (shopifyPathMatch) return shopifyPathMatch[1];
          return fallback;
        }
      }

      static imageFileBaseKey(src) {
        const normalized = QuickAddModal.normalizeImageSrc(src);
        if (!normalized) return '';

        try {
          let filename = decodeURIComponent(new URL(normalized).pathname).toLowerCase().split('/').pop() || '';
          if (!filename) return '';
          return filename.replace(
            /_(\d+x\d*|\d+x|pico|icon|thumb|small|compact|medium|large|grande|original|master)(?=\.[a-z0-9]+$)/i,
            ''
          );
        } catch {
          const fallback = normalized.split('?')[0].toLowerCase().split('/').pop() || '';
          return fallback.replace(/_(\d+x\d*|\d+x)(?=\.[a-z0-9]+$)/i, '');
        }
      }

      static getCatalogImages(product) {
        if (!product) return [];

        const bySrcKey = new Map();
        const byFileKey = new Map();

        const addImage = (image, variantIds = []) => {
          if (!image) return;
          const id = image.id != null ? Number(image.id) : null;
          const src = QuickAddModal.normalizeImageSrc(
            image.src || image.url || image.preview_image?.src || ''
          );
          if (!src) return;

          const srcKey = QuickAddModal.imageDedupKey(src);
          const fileKey = QuickAddModal.imageFileBaseKey(src);
          if (!srcKey && !fileKey) return;

          const position = image.position ?? bySrcKey.size + 1;
          const existing =
            (srcKey && bySrcKey.get(srcKey)) || (fileKey && byFileKey.get(fileKey)) || null;

          if (existing) {
            const mergedIds = new Set([
              ...(existing.variant_ids || []),
              ...variantIds,
              ...(image.variant_ids || []),
            ]);
            existing.variant_ids = Array.from(mergedIds);
            if (position < existing.position) existing.position = position;
            if (id != null && existing.id == null) existing.id = id;
            return;
          }

          const entry = {
            id,
            src,
            position,
            variant_ids: variantIds.length ? variantIds : image.variant_ids || [],
            url: src,
          };

          if (srcKey) bySrcKey.set(srcKey, entry);
          if (fileKey) byFileKey.set(fileKey, entry);
        };

        [...(product.images || [])].forEach((image) => addImage(image));

        (product.media || []).forEach((media, index) => {
          if (media.media_type && media.media_type !== 'image') return;
          const preview = media.preview_image || media;
          addImage(
            {
              id: media.id ?? preview.id,
              src: preview.src,
              position: media.position ?? index + 1,
              variant_ids: [],
            },
            []
          );
        });

        return Array.from(new Set([...bySrcKey.values(), ...byFileKey.values()])).sort(
          (a, b) => (a.position || 0) - (b.position || 0)
        );
      }

      static getProductDefaultImage(product) {
        const first = QuickAddModal.getCatalogImages(product)[0];
        const src = QuickAddModal.normalizeImageSrc(first?.src || first?.url || '');
        return {
          id: first?.id ?? null,
          src,
          key: QuickAddModal.imageDedupKey(src),
        };
      }

      static cacheGallerySlideSources(product, productInfo) {
        const slider = productInfo?.querySelector('[id^="Slider-Gallery-"]');
        if (!slider) return;

        const srcToImageId = new Map();
        QuickAddModal.getCatalogImages(product).forEach((image) => {
          const src = QuickAddModal.normalizeImageSrc(image.src || image.url || '');
          const key = QuickAddModal.imageDedupKey(src);
          if (key) srcToImageId.set(key, image.id);
        });

        slider.querySelectorAll('li.product__media-item').forEach((slide) => {
          const img = slide.querySelector('img');
          if (!img) return;

          if (!slide.dataset.quickAddOriginalSrc) {
            slide.dataset.quickAddOriginalSrc = QuickAddModal.normalizeImageSrc(img.currentSrc || img.src);
          }

          if (!slide.dataset.quickAddImageId) {
            const key = QuickAddModal.imageDedupKey(slide.dataset.quickAddOriginalSrc);
            if (srcToImageId.has(key)) {
              slide.dataset.quickAddImageId = String(srcToImageId.get(key));
            }
          }
        });

        const productDefault = QuickAddModal.getProductDefaultImage(product);
        if (productDefault.id != null) {
          const primarySlide = slider.querySelector(
            `li.product__media-item[data-quick-add-image-id="${productDefault.id}"]`
          );
          if (primarySlide) primarySlide.dataset.quickAddPrimarySlide = 'true';
        }
      }

      static getVariantAssignedImageIds(product) {
        const productDefault = QuickAddModal.getProductDefaultImage(product);
        const defaultId = productDefault.id != null ? Number(productDefault.id) : null;
        const assigned = new Set();

        (product?.variants || []).forEach((variant) => {
          const imageId = variant.image_id != null ? Number(variant.image_id) : null;
          const featured = variant.featured_image;
          const featSrc = QuickAddModal.normalizeImageSrc(featured?.src || '');
          const featId = featured?.id != null ? Number(featured.id) : null;
          const featIsNonDefault =
            Boolean(featSrc) &&
            (!productDefault.key || QuickAddModal.imageDedupKey(featSrc) !== productDefault.key);

          if (!featIsNonDefault) return;

          if (featId != null) assigned.add(featId);
          if (imageId != null) assigned.add(imageId);
        });

        if (defaultId != null) assigned.delete(defaultId);

        return assigned;
      }

      static getVariantDedicatedImage(product, variant) {
        if (!variant || !product) return null;

        const productDefault = QuickAddModal.getProductDefaultImage(product);
        const variantId = Number(variant.id);
        const defaultId = productDefault.id != null ? Number(productDefault.id) : null;
        const fullVariant = product?.variants?.find((v) => Number(v.id) === variantId) || variant;
        const catalogImages = QuickAddModal.getCatalogImages(product);
        const assignedImageIds = QuickAddModal.getVariantAssignedImageIds(product);
        const imageSrc = (image) =>
          typeof image === 'string'
            ? QuickAddModal.normalizeImageSrc(image)
            : QuickAddModal.normalizeImageSrc(image?.src || image?.url || image?.preview_image?.src || '');
        const isNonDefaultSrc = (src) =>
          Boolean(src) && (!productDefault.key || QuickAddModal.imageDedupKey(src) !== productDefault.key);

        const variantImageId =
          fullVariant.image_id != null ? Number(fullVariant.image_id) : null;
        const featured = fullVariant.featured_image || fullVariant.featured_media?.preview_image;
        const featSrc = imageSrc(featured);
        const featuredId = featured?.id != null ? Number(featured.id) : null;

        if (variantImageId != null && defaultId != null && variantImageId === defaultId) {
          return null;
        }

        if (variantImageId != null && assignedImageIds.has(variantImageId)) {
          const linkedImage = catalogImages.find((image) => Number(image.id) === variantImageId);
          const src = imageSrc(linkedImage) || featSrc;
          if (isNonDefaultSrc(src)) {
            return { src, id: variantImageId };
          }
        }

        if (isNonDefaultSrc(featSrc) && featuredId != null && assignedImageIds.has(featuredId)) {
          return {
            src: featSrc,
            id: featuredId,
          };
        }

        return null;
      }

      static resolveVariantDisplayImage(product, variant) {
        if (!variant || !product) return null;

        const productDefault = QuickAddModal.getProductDefaultImage(product);

        const dedicated = QuickAddModal.getVariantDedicatedImage(product, variant);
        if (dedicated?.src) {
          return { ...dedicated, isVariantSpecific: true };
        }

        if (productDefault.src) {
          return {
            src: productDefault.src,
            id: productDefault.id,
            isVariantSpecific: false,
          };
        }

        return null;
      }

      applyDisplayImageToGallery(productInfo, displayImage, variant = null, { browse = false } = {}) {
        if (!productInfo || !displayImage?.src) return;

        const gallery = productInfo.querySelector('media-gallery');
        const slider = productInfo.querySelector('[id^="Slider-Gallery-"]');
        const slides = slider ? Array.from(slider.querySelectorAll('li.product__media-item')) : [];
        if (!slides.length) return;

        QuickAddModal.cacheGallerySlideSources(this.quickAddProduct, productInfo);

        const thumbnailItem = {
          src: displayImage.src,
          imageId: displayImage.id,
          mediaId: '',
        };

        const activeSlide = slides.find((slide) => slide.classList.contains('is-active')) || slides[0];

        const applySrcToSlide = (slide, src = displayImage.src) => {
          if (!slide || !src) return;
          slide.querySelectorAll('picture source').forEach((source) => {
            source.srcset = src;
          });
          const img = slide.querySelector('img');
          if (!img) return;
          img.src = src;
          img.removeAttribute('srcset');
          img.removeAttribute('data-srcset');
          img.removeAttribute('data-src');
        };

        const productVariant =
          variant && this.quickAddProduct?.variants?.find((v) => Number(v.id) === Number(variant.id));

        const productDefault = QuickAddModal.getProductDefaultImage(this.quickAddProduct);

        if (browse) {
          const visibleSlide = this.findSlideForThumbnailItem(slider, slides, thumbnailItem) || activeSlide;
          applySrcToSlide(visibleSlide);
          slides.forEach((slide) => slide.classList.remove('is-active'));
          visibleSlide.classList.add('is-active');

          const mediaId = visibleSlide.dataset.mediaId;
          if (gallery && typeof gallery.setActiveMedia === 'function' && mediaId) {
            gallery.setActiveMedia(mediaId, false);
          }

          window.requestAnimationFrame(() => applySrcToSlide(visibleSlide));
          return;
        }

        if (!displayImage.isVariantSpecific) {
          const defaultSlide = this.findDefaultProductSlide(slider, slides, this.quickAddProduct);
          const restoreSrc = productDefault.src;

          slides.forEach((slide) => {
            const original = slide.dataset.quickAddOriginalSrc;
            applySrcToSlide(slide, original || restoreSrc);
          });

          applySrcToSlide(defaultSlide, restoreSrc);

          slides.forEach((slide) => slide.classList.remove('is-active'));
          defaultSlide.classList.add('is-active');

          window.requestAnimationFrame(() => {
            applySrcToSlide(defaultSlide, restoreSrc);
          });

          this.syncQuickAddThumbnails(
            null,
            productVariant || variant,
            { src: restoreSrc, id: productDefault.id, isVariantSpecific: false }
          );
          return;
        }

        const wheelSlide = this.findDefaultProductSlide(slider, slides, this.quickAddProduct);

        applySrcToSlide(wheelSlide, displayImage.src);

        slides.forEach((slide) => slide.classList.remove('is-active'));
        wheelSlide.classList.add('is-active');

        window.requestAnimationFrame(() => {
          applySrcToSlide(wheelSlide, displayImage.src);
        });

        this.syncQuickAddThumbnails(null, productVariant || variant, displayImage);
      }

      findDefaultProductSlide(slider, slides, product) {
        if (!slides.length) return null;

        const marked = slides.find((slide) => slide.dataset.quickAddPrimarySlide === 'true');
        if (marked) return marked;

        const productDefault = QuickAddModal.getProductDefaultImage(product);
        const defaultItem = {
          src: productDefault.src,
          imageId: productDefault.id,
          mediaId: '',
        };

        return this.findSlideForThumbnailItem(slider, slides, defaultItem) || slides[0];
      }

      static collectThumbnailItemsFromDom(mediaWrapper) {
        const items = [];
        if (!mediaWrapper) return items;

        mediaWrapper.querySelectorAll('.thumbnail-list__item').forEach((listItem) => {
          const img = listItem.querySelector('img');
          if (!img) return;

          items.push({
            src: QuickAddModal.normalizeImageSrc(img.currentSrc || img.src),
            imageId: null,
            variantId: null,
            variantIds: [],
            mediaId: listItem.dataset.target || '',
          });
        });

        return items;
      }

      static collectThumbnailItemsFromGallery(productInfo) {
        const items = [];
        if (!productInfo) return items;

        const seen = new Set();
        const addImg = (img, mediaId = '') => {
          const src = QuickAddModal.normalizeImageSrc(img?.currentSrc || img?.src);
          if (!src) return;
          const key = QuickAddModal.imageDedupKey(src);
          if (seen.has(key)) return;
          seen.add(key);
          items.push({
            src,
            imageId: null,
            variantId: null,
            variantIds: [],
            mediaId,
          });
        };

        productInfo.querySelectorAll('li.product__media-item').forEach((slide) => {
          const img = slide.querySelector('img');
          if (img) addImg(img, slide.getAttribute('data-media-id') || '');
        });

        productInfo
          .querySelectorAll('.thumbnail-list__item img, .product__media-wrapper img')
          .forEach((img) => addImg(img));

        return items;
      }

      static collectAllThumbnailSources(product, productInfo, mediaWrapper) {
        const items = [];
        const bySrcKey = new Map();
        const byFileKey = new Map();

        const addItem = (item) => {
          const src = QuickAddModal.normalizeImageSrc(item?.src);
          if (!src) return;

          const srcKey = QuickAddModal.imageDedupKey(src);
          const fileKey = QuickAddModal.imageFileBaseKey(src);
          if (!srcKey && !fileKey) return;

          const existing =
            (srcKey && bySrcKey.get(srcKey)) || (fileKey && byFileKey.get(fileKey)) || null;

          if (existing) {
            if (!existing.imageId && item.imageId) existing.imageId = item.imageId;
            if (!existing.mediaId && item.mediaId) existing.mediaId = item.mediaId;
            if (item.variantIds?.length) {
              existing.variantIds = Array.from(
                new Set([...(existing.variantIds || []), ...item.variantIds])
              );
            }
            return;
          }

          const normalized = {
            src,
            imageId: item.imageId || null,
            variantId: item.variantId || item.variantIds?.[0] || null,
            variantIds: item.variantIds || (item.variantId ? [item.variantId] : []),
            mediaId: item.mediaId || '',
          };

          if (srcKey) bySrcKey.set(srcKey, normalized);
          if (fileKey) byFileKey.set(fileKey, normalized);
          items.push(normalized);
        };

        QuickAddModal.collectThumbnailItems(product, productInfo).forEach(addItem);
        QuickAddModal.collectThumbnailItemsFromGallery(productInfo).forEach(addItem);
        QuickAddModal.collectVariantOnlyThumbnailItems(product).forEach(addItem);

        return items;
      }

      static thumbnailImageUrl(src, width = 200) {
        const normalized = QuickAddModal.normalizeImageSrc(src);
        if (!normalized) return '';
        if (normalized.includes('width=')) return normalized;
        const joiner = normalized.includes('?') ? '&' : '?';
        return `${normalized}${joiner}width=${width}`;
      }

      static mergeThumbnailItems(...lists) {
        const items = [];
        const byKey = new Map();

        const mergeOne = (item) => {
          const src = QuickAddModal.normalizeImageSrc(item?.src);
          if (!src) return;

          const key = item.imageId
            ? `id:${item.imageId}`
            : QuickAddModal.imageDedupKey(src);
          const existing = byKey.get(key);

          if (existing) {
            if (!existing.mediaId && item.mediaId) existing.mediaId = item.mediaId;
            if (!existing.imageId && item.imageId) existing.imageId = item.imageId;

            const variantIds = new Set([
              ...(existing.variantIds || []),
              ...(item.variantIds || []),
              ...(item.variantId ? [item.variantId] : []),
              ...(existing.variantId ? [existing.variantId] : []),
            ]);
            existing.variantIds = Array.from(variantIds);
            if (!existing.variantId && existing.variantIds.length) {
              existing.variantId = existing.variantIds[0];
            }
            return;
          }

          const normalized = {
            src,
            imageId: item.imageId || null,
            variantId: item.variantId || item.variantIds?.[0] || null,
            variantIds: item.variantIds || (item.variantId ? [item.variantId] : []),
            mediaId: item.mediaId || '',
          };
          byKey.set(key, normalized);
          items.push(normalized);
        };

        lists.flat().forEach(mergeOne);
        return items;
      }

      static collectThumbnailItems(product, productInfo) {
        const mediaBySrc = new Map();
        productInfo?.querySelectorAll('li.product__media-item[data-media-id]').forEach((slide) => {
          const img = slide.querySelector('img');
          const srcKey = img ? QuickAddModal.imageDedupKey(img.currentSrc || img.src) : '';
          if (srcKey) mediaBySrc.set(srcKey, slide.getAttribute('data-media-id') || '');
        });

        productInfo
          ?.querySelectorAll('.thumbnail-list__item[data-target]')
          .forEach((listItem) => {
            const img = listItem.querySelector('img');
            const srcKey = img ? QuickAddModal.imageDedupKey(img.currentSrc || img.src) : '';
            if (srcKey && !mediaBySrc.has(srcKey)) {
              mediaBySrc.set(srcKey, listItem.dataset.target || '');
            }
          });

        const rawItems = [];

        QuickAddModal.getCatalogImages(product).forEach((image) => {
          const src = image.src || image.url;
          if (!src) return;
          rawItems.push({
            src: QuickAddModal.normalizeImageSrc(src),
            imageId: image.id || null,
            variantId: image.variant_ids?.[0] || null,
            variantIds: image.variant_ids || [],
            mediaId: mediaBySrc.get(QuickAddModal.imageDedupKey(src)) || '',
          });
        });

        return rawItems;
      }

      static collectVariantOnlyThumbnailItems(product) {
        if (!product) return [];

        const assignedIds = QuickAddModal.getVariantAssignedImageIds(product);
        if (!assignedIds.size) return [];

        const catalogIds = new Set();
        const catalogKeys = new Set();
        QuickAddModal.getCatalogImages(product).forEach((image) => {
          if (image.id != null) catalogIds.add(Number(image.id));
          const key = QuickAddModal.imageDedupKey(image.src || image.url || '');
          if (key) catalogKeys.add(key);
        });

        const items = [];
        const seen = new Set();

        (product.variants || []).forEach((variant) => {
          const featured = variant.featured_image;
          const imageId =
            featured?.id != null
              ? Number(featured.id)
              : variant.image_id != null
                ? Number(variant.image_id)
                : null;

          if (imageId == null || !assignedIds.has(imageId)) return;
          if (catalogIds.has(imageId)) return;

          const src = QuickAddModal.normalizeImageSrc(featured?.src || '');
          if (!src) return;

          const srcKey = QuickAddModal.imageDedupKey(src);
          if (catalogKeys.has(srcKey)) return;

          const dedupeKey = `id:${imageId}`;
          if (seen.has(dedupeKey)) return;
          seen.add(dedupeKey);

          items.push({
            src,
            imageId,
            variantId: variant.id,
            variantIds: [variant.id],
            mediaId: '',
          });
        });

        return items;
      }

      async fetchProductJson(productElement) {
        if (this.quickAddProduct) return this.quickAddProduct;

        const productUrl = productElement?.dataset?.url || this.openedBy?.getAttribute('data-product-url');
        if (!productUrl) return null;

        try {
          const response = await fetch(`${productUrl}.js`);
          if (!response.ok) return null;
          this.quickAddProduct = await response.json();
          return this.quickAddProduct;
        } catch (error) {
          return null;
        }
      }

      findSlideForThumbnailItem(slider, slides, item) {
        if (item.mediaId) {
          const byMediaId = slider?.querySelector(`[data-media-id="${item.mediaId}"]`);
          if (byMediaId) return byMediaId;
        }

        if (item.imageId) {
          const byImageId = slides.find(
            (slide) => slide.dataset.quickAddImageId && String(slide.dataset.quickAddImageId) === String(item.imageId)
          );
          if (byImageId) return byImageId;
        }

        const itemKey = QuickAddModal.imageDedupKey(item.src);
        if (itemKey) {
          const byOriginalSrc = slides.find((slide) => {
            const original = slide.dataset.quickAddOriginalSrc;
            return original && QuickAddModal.imageDedupKey(original) === itemKey;
          });
          if (byOriginalSrc) return byOriginalSrc;
        }

        return (
          slides.find((slide) => {
            const img = slide.querySelector('img');
            if (!img) return false;
            return QuickAddModal.imageDedupKey(img.currentSrc || img.src) === itemKey;
          }) || null
        );
      }

      resolveVariantForThumbnail(item) {
        if (!this.quickAddProduct || !item) return null;

        const ids = item.variantIds?.length
          ? item.variantIds
          : item.variantId
            ? [item.variantId]
            : [];
        if (!ids.length) return null;

        const candidates = this.quickAddProduct.variants.filter((variant) =>
          ids.some((id) => Number(id) === Number(variant.id))
        );
        if (!candidates.length) return null;

        const wheelOptions = this.modalContent?.querySelector('.quick-add-wheel-options');
        if (!wheelOptions) return candidates.length === 1 ? candidates[0] : null;

        const indices = QuickAddModal.getWheelOptionIndices(this.quickAddProduct.options);
        if (!indices) return null;

        const { boltIdx, sizeIdx, finishIdx } = indices;
        if (finishIdx >= 0) {
          const finishValues = new Set(
            candidates.map((variant) => QuickAddModal.getVariantOptionValue(variant, finishIdx))
          );
          if (finishValues.size !== 1) return null;
        }

        const boltSelect = wheelOptions.querySelector('.quick-add-tire-options__group--diameter select');
        const sizeBtn = wheelOptions.querySelector('.quick-add-tire-size.is-selected');
        const boltVal = boltSelect?.value || '';
        const sizeVal = sizeBtn?.dataset.sizeValue || '';

        const match = candidates.find((variant) => {
          const boltOk =
            boltIdx < 0 || QuickAddModal.getVariantOptionValue(variant, boltIdx) === boltVal;
          const sizeOk = QuickAddModal.getVariantOptionValue(variant, sizeIdx) === sizeVal;
          return boltOk && sizeOk;
        });

        return match || null;
      }

      activateVariantFromThumbnail(variant) {
        if (!variant) return;

        const wheelOptions = this.modalContent?.querySelector('.quick-add-wheel-options');

        if (wheelOptions && this.quickAddProduct) {
          const indices = QuickAddModal.getWheelOptionIndices(this.quickAddProduct.options);
          if (indices && indices.finishIdx >= 0) {
            const diameterSelects = wheelOptions.querySelectorAll(
              '.quick-add-tire-options__group--diameter select'
            );
            const finishSelect =
              indices.boltIdx >= 0 ? diameterSelects[1] : diameterSelects[0] || null;
            const finishVal = variant[`option${indices.finishIdx + 1}`];

            if (finishSelect && finishVal) {
              if (finishSelect.value !== finishVal) {
                finishSelect.value = finishVal;
                finishSelect.dispatchEvent(new Event('change', { bubbles: true }));
              } else {
                this.updateVariantMedia(variant);
              }
              return;
            }
          }
        }

        const variantInput = this.modalContent?.querySelector('product-form input[name="id"]');
        if (variantInput) variantInput.value = variant.id;

        this.updateVariantMedia(variant);
      }

      handleThumbnailClick(item, btn, list) {
        const productInfo = this.modalContent?.querySelector('product-info');
        if (!productInfo) return;

        this.applyDisplayImageToGallery(
          productInfo,
          { src: item.src, id: item.imageId || null },
          null,
          { browse: true }
        );

        const variant = this.resolveVariantForThumbnail(item);
        if (variant) {
          this.activateVariantFromThumbnail(variant);
        }

        list.querySelectorAll('.is-active').forEach((el) => el.classList.remove('is-active'));
        btn.classList.add('is-active');
      }

      async ensureThumbnailsStrip(productElement) {
        const root = this.modalContent;
        if (!root) return;

        const productInfo = productElement || root.querySelector('product-info');
        if (!productInfo) return;

        const mediaWrapper = productInfo.querySelector('.product__media-wrapper');
        if (!mediaWrapper) return;

        const slider = mediaWrapper.querySelector('[id^="Slider-Gallery-"]');
        if (!slider) return;

        let product = this.quickAddProduct || (await this.fetchProductJson(productInfo));

        if (!product) {
          const productUrl = this.openedBy?.getAttribute('data-product-url');
          if (productUrl) {
            try {
              const response = await fetch(`${productUrl}.js`);
              if (response.ok) {
                product = await response.json();
                this.quickAddProduct = product;
              }
            } catch (error) {
              product = null;
            }
          }
        }

        const existingStrip = mediaWrapper.querySelector('.quick-add-thumbnails');
        const existingButtons = existingStrip?.querySelectorAll('.quick-add-thumbnails__item')?.length || 0;
        if (existingStrip && existingButtons > 0) {
          const activeVariantId = this.modalContent?.querySelector('product-form input[name="id"]')?.value;
          const activeVariant = activeVariantId
            ? product?.variants?.find((v) => Number(v.id) === Number(activeVariantId))
            : null;
          if (activeVariant) {
            this.updateVariantMedia(activeVariant);
          }
          return;
        }
        if (existingStrip) existingStrip.remove();

        const slides = Array.from(slider.querySelectorAll('li.product__media-item'));
        let thumbnailItems = QuickAddModal.collectAllThumbnailSources(product, productInfo, mediaWrapper);

        thumbnailItems = thumbnailItems.map((item) => {
          if (item.mediaId) return item;
          const slide = this.findSlideForThumbnailItem(slider, slides, item);
          return {
            ...item,
            mediaId: slide?.getAttribute('data-media-id') || item.mediaId || '',
          };
        });

        if (!thumbnailItems.length) return;

        this.quickAddThumbnailItems = thumbnailItems;

        // Dawn thumbnails are hidden via .quick-add-hidden — always use the custom strip.
        mediaWrapper.querySelectorAll('.thumbnail-slider, .thumbnail-list').forEach((element) => element.remove());

        const strip = document.createElement('div');
        strip.className = 'quick-add-thumbnails';
        strip.innerHTML = `<div class="quick-add-thumbnails__label"></div><div class="quick-add-thumbnails__list" role="list"></div>`;

        const list = strip.querySelector('.quick-add-thumbnails__list');

        thumbnailItems.forEach((item, index) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'quick-add-thumbnails__item';
          btn.setAttribute('aria-label', `Image ${index + 1}`);
          if (item.imageId) btn.dataset.imageId = String(item.imageId);
          if (item.variantId) btn.dataset.variantId = String(item.variantId);
          if (item.variantIds?.length) btn.dataset.variantIds = item.variantIds.map(String).join(',');
          if (item.mediaId) btn.dataset.mediaId = item.mediaId;
          btn.dataset.imageSrc = item.src;

          const thumb = document.createElement('img');
          thumb.loading = 'lazy';
          thumb.alt = '';
          thumb.src = QuickAddModal.thumbnailImageUrl(item.src);
          btn.appendChild(thumb);

          btn.addEventListener('click', () => {
            this.handleThumbnailClick(item, btn, list);
          });

          if (index === 0) btn.classList.add('is-active');
          list.appendChild(btn);
        });

        const manufacturerBlock = mediaWrapper.querySelector('.quick-add-manufacturer');
        if (manufacturerBlock) {
          mediaWrapper.insertBefore(strip, manufacturerBlock);
        } else {
          mediaWrapper.appendChild(strip);
        }

        const activeVariantId = this.modalContent?.querySelector('product-form input[name="id"]')?.value;
        const activeVariant = activeVariantId
          ? product?.variants?.find((v) => Number(v.id) === Number(activeVariantId))
          : null;
        if (activeVariant) {
          this.updateVariantMedia(activeVariant);
        }
      }

      isTireProduct(productElement) {
        let type = (this.openerProductType || '').trim();
        if (!type && productElement) {
          type = (productElement.getAttribute('data-product-type') || '').trim();
        }
        const normalized = type.toLowerCase();
        return normalized === 'tire' || normalized === 'tires' || normalized.includes('tire');
      }

      isWheelProduct(productElement) {
        let type = (this.openerProductType || '').trim();
        if (!type && productElement) {
          type = (productElement.getAttribute('data-product-type') || '').trim();
        }
        const normalized = type.toLowerCase();
        return normalized === 'wheel' || normalized === 'wheels';
      }

      isWheelTireModalProduct(productElement) {
        return this.isWheelProduct(productElement) || this.isTireProduct(productElement);
      }

      isSuspensionProduct(productElement, product = null) {
        let type = (this.openerProductType || '').trim();
        if (!type && productElement) {
          type = (productElement.getAttribute('data-product-type') || '').trim();
        }
        if (!type && product?.type) {
          type = String(product.type).trim();
        }
        if (!type && product?.product_type) {
          type = String(product.product_type).trim();
        }
        return type.toLowerCase().includes('suspension');
      }

      static isCustomSpringsValue(value) {
        return String(value || '')
          .trim()
          .toLowerCase() === 'custom';
      }

      static findSpringsOptionIndex(optionLabels) {
        return (optionLabels || []).findIndex(
          (label) => String(label).trim().toLowerCase() === 'spring rates'
        );
      }

      resolveSuspensionSpringRatesForm(priceBox) {
        return (
          priceBox?.querySelector('form[data-type="add-to-cart-form"]') ||
          priceBox?.querySelector('product-form form')
        );
      }

      bindSuspensionSpringRatesValidation(form) {
        if (!form || form.dataset.quickAddSuspensionRatesBound === 'true') return;
        form.dataset.quickAddSuspensionRatesBound = 'true';

        form.addEventListener(
          'submit',
          (event) => {
            const modal = form.closest('quick-add-modal');
            if (!modal) return;

            const rates = modal.querySelector('.quick-add-suspension-spring-rates:not([hidden])');
            if (!rates) return;

            const frontInput = rates.querySelector('input[data-suspension-spring-rate="front"]');
            const rearInput = rates.querySelector('input[data-suspension-spring-rate="rear"]');
            if (!frontInput || !rearInput) return;

            let valid = true;
            if (!frontInput.value.trim()) {
              frontInput.setCustomValidity('Enter a front spring rate.');
              valid = false;
            } else {
              frontInput.setCustomValidity('');
            }

            if (!rearInput.value.trim()) {
              rearInput.setCustomValidity('Enter a rear spring rate.');
              valid = false;
            } else {
              rearInput.setCustomValidity('');
            }

            if (!valid) {
              event.preventDefault();
              event.stopImmediatePropagation();
              (frontInput.value.trim() ? rearInput : frontInput).reportValidity();
            }
          },
          true
        );
      }

      setupSuspensionSpringRates(productElement, product, priceBox, variantPickerRoot) {
        if (!this.isSuspensionProduct(productElement, product)) return;

        const optionLabels = QuickAddModal.normalizeProductOptionLabels(product.options);
        const springsIndex = QuickAddModal.findSpringsOptionIndex(optionLabels);
        if (springsIndex < 0) return;

        const form = this.resolveSuspensionSpringRatesForm(priceBox);
        if (!form || !variantPickerRoot) return;
        if (variantPickerRoot.querySelector('.quick-add-suspension-spring-rates')) return;

        const springsGroup = variantPickerRoot.querySelector(
          `.quick-add-variant-option[data-option-index="${springsIndex}"]`
        );
        const springsSelect = variantPickerRoot.querySelector(
          `.quick-add-variant-option__select[data-option-index="${springsIndex}"]`
        );
        const springsSegmented = variantPickerRoot.querySelector(
          `.quick-add-variant-segmented[data-option-index="${springsIndex}"]`
        );
        if (!springsGroup) return;

        const sectionId = productElement.dataset.section || 'quickadd';
        const frontId = `QuickAddFrontSpring-${sectionId}`;
        const rearId = `QuickAddRearSpring-${sectionId}`;
        const formId = form.id || '';

        const ratesRoot = document.createElement('div');
        ratesRoot.className = 'quick-add-suspension-spring-rates';
        ratesRoot.hidden = true;
        ratesRoot.innerHTML = `
          <div class="quick-add-suspension-spring-rate">
            <label class="form__label" for="${frontId}">Front Spring Rate</label>
            <div class="quick-add-suspension-spring-rate__field">
              <input
                id="${frontId}"
                class="quick-add-suspension-spring-rate__input"
                type="text"
                name="properties[Front Spring Rate]"
                data-suspension-spring-rate="front"
                placeholder="e.g. 8K"
                autocomplete="off"
                disabled
              >
              <span class="quick-add-suspension-spring-rate__unit" aria-hidden="true">kg/mm</span>
            </div>
          </div>
          <div class="quick-add-suspension-spring-rate">
            <label class="form__label" for="${rearId}">Rear Spring Rate</label>
            <div class="quick-add-suspension-spring-rate__field">
              <input
                id="${rearId}"
                class="quick-add-suspension-spring-rate__input"
                type="text"
                name="properties[Rear Spring Rate]"
                data-suspension-spring-rate="rear"
                placeholder="e.g. 6K"
                autocomplete="off"
                disabled
              >
              <span class="quick-add-suspension-spring-rate__unit" aria-hidden="true">kg/mm</span>
            </div>
          </div>
        `;

        const frontInput = ratesRoot.querySelector('input[data-suspension-spring-rate="front"]');
        const rearInput = ratesRoot.querySelector('input[data-suspension-spring-rate="rear"]');
        if (formId) {
          frontInput?.setAttribute('form', formId);
          rearInput?.setAttribute('form', formId);
        }

        springsGroup.insertAdjacentElement('afterend', ratesRoot);

        const updateSpringRateFields = () => {
          const isCustom = QuickAddModal.isCustomSpringsValue(
            QuickAddModal.getVariantPickerOptionValue(variantPickerRoot, springsIndex)
          );
          ratesRoot.hidden = !isCustom;

          [frontInput, rearInput].forEach((input) => {
            if (!input) return;
            input.disabled = !isCustom;
            input.required = isCustom;
            if (!isCustom) {
              input.value = '';
              input.setCustomValidity('');
            }
          });
        };

        springsSelect?.addEventListener('change', updateSpringRateFields);
        springsSegmented?.addEventListener('click', (event) => {
          if (event.target.matches('.quick-add-variant-segmented__btn')) {
            updateSpringRateFields();
          }
        });
        updateSpringRateFields();
        this.bindSuspensionSpringRatesValidation(form);
        this.classList.add('quick-add--suspension');
      }

      resolveTireSpec(productElement) {
        let type = (this.openerTireType || '').trim();
        let season = (this.openerTireSeason || '').trim();
        const meta = productElement?.querySelector('[data-quick-add-tire-meta]');
        if (meta) {
          if (!type) type = (meta.getAttribute('data-tire-type') || '').trim();
          if (!season) season = (meta.getAttribute('data-tire-season') || '').trim();
        }
        return { type, season };
      }

      injectManufacturerBelowImages(productElement) {
        if (!productElement) return;
        const vendor = (this.openerVendor || '').trim();
        const isWheel = this.isWheelProduct(productElement);
        const isTire = this.isTireProduct(productElement);
        const showTypeSeason = isTire && !isWheel;
        const { type: tireType, season: tireSeason } = this.resolveTireSpec(productElement);
        const vehiclesRaw = isWheel || isTire ? '' : (this.openerVehicles || '').trim();
        const vehicles = vehiclesRaw
          ? vehiclesRaw
              .split('||')
              .map((v) => v.trim())
              .filter(Boolean)
          : [];
        const mediaWrapper = productElement.querySelector('.product__media-wrapper');
        if (!mediaWrapper) return;
        if (mediaWrapper.querySelector('.quick-add-manufacturer')) return;

        const descSource = productElement.querySelector('.product__description');
        const descHtml = descSource ? descSource.innerHTML.trim() : '';

        const el = document.createElement('div');
        el.className = 'quick-add-manufacturer';
        el.innerHTML = `
          ${vendor ? `<div class="quick-add-manufacturer__label">MANUFACTURER</div>
          <div class="quick-add-manufacturer__value"></div>` : ``}
          ${
            showTypeSeason
              ? `<div class="quick-add-tire-spec">
            <div class="quick-add-tire-spec__label">TYPE & SEASON</div>
            <div class="quick-add-tire-spec__type"></div>
            <div class="quick-add-tire-spec__season"></div>
          </div>`
              : ``
          }
          ${descHtml ? `<div class="quick-add-description">
            <div class="quick-add-description__label">DESCRIPTION</div>
            <div class="quick-add-description__value"></div>
          </div>` : ``}
          ${vehicles.length ? `<div class="quick-add-vehicles">
            <div class="quick-add-vehicles__label">COMPATIBLE VEHICLES</div>
            <div class="quick-add-vehicles__value"></div>
          </div>` : ``}
        `;
        const vendorEl = el.querySelector('.quick-add-manufacturer__value');
        if (vendorEl) vendorEl.textContent = vendor;
        const tireTypeEl = el.querySelector('.quick-add-tire-spec__type');
        if (tireTypeEl) tireTypeEl.textContent = tireType;
        const tireSeasonEl = el.querySelector('.quick-add-tire-spec__season');
        if (tireSeasonEl) tireSeasonEl.textContent = tireSeason;
        const descEl = el.querySelector('.quick-add-description__value');
        if (descEl) descEl.innerHTML = descHtml;
        const vehiclesEl = el.querySelector('.quick-add-vehicles__value');
        if (vehiclesEl && vehicles.length) {
          vehiclesEl.innerHTML = '';
          vehicles.forEach((v) => {
            const pill = document.createElement('span');
            pill.className = 'quick-add-vehicles__pill';
            pill.textContent = v;
            vehiclesEl.appendChild(pill);
          });
        }
        mediaWrapper.appendChild(el);
      }

      static combineVendorAndTitle(vendor, productTitle) {
        const v = (vendor || '').trim();
        const t = (productTitle || '').trim();
        if (!v) return t;
        if (!t) return v;
        const vl = v.toLowerCase();
        const tl = t.toLowerCase();
        if (tl === vl || tl.startsWith(`${vl} `) || tl.startsWith(`${vl}\u00a0`)) {
          return t;
        }
        return `${v} ${t}`;
      }

      static normalizeProductOptionLabels(options) {
        return (options || []).map((o) => {
          if (o == null) return '';
          if (typeof o === 'string') return o;
          if (typeof o === 'object' && typeof o.name === 'string') return o.name;
          if (typeof o === 'object' && o.name != null) return String(o.name);
          return '';
        });
      }

      static getTireOptionIndices(optionNames) {
        const labels = QuickAddModal.normalizeProductOptionLabels(optionNames);
        const names = labels.map((name) => String(name).toLowerCase());
        let diameterIdx = names.findIndex((name) => name.includes('diameter') || name.includes('rim'));
        let sizeIdx = names.findIndex(
          (name) => name.includes('size') || name === 'tire size' || name === 'wheel size'
        );

        if (names.length === 2) {
          if (diameterIdx === -1 && sizeIdx === -1) {
            diameterIdx = 0;
            sizeIdx = 1;
          } else if (diameterIdx === -1) {
            diameterIdx = sizeIdx === 0 ? 1 : 0;
          } else if (sizeIdx === -1) {
            sizeIdx = diameterIdx === 0 ? 1 : 0;
          }
        } else if (names.length === 1 && sizeIdx === -1 && diameterIdx === -1) {
          sizeIdx = 0;
        }

        return { diameterOptionIndex: diameterIdx, sizeOptionIndex: sizeIdx };
      }

      static getWheelOptionIndices(optionNames) {
        const labels = QuickAddModal.normalizeProductOptionLabels(optionNames);
        const names = labels.map((l) => String(l).toLowerCase());
        const n = names.length;
        if (n < 2 || n > 3) return null;

        let boltIdx = names.findIndex((x) => /bolt|pattern|pcd|lug/.test(x));
        let sizeIdx = names.findIndex(
          (x) =>
            x.includes('size') ||
            x.includes('fitment') ||
            x.includes('diameter') ||
            (x.includes('width') && !x.includes('bolt'))
        );
        let finishIdx = names.findIndex((x) => /finish|color|colour/.test(x));

        if (n === 3) {
          const used = new Set([boltIdx, sizeIdx, finishIdx].filter((i) => i >= 0));
          if (boltIdx < 0) {
            boltIdx = [0, 1, 2].find((i) => !used.has(i));
            used.add(boltIdx);
          }
          if (sizeIdx < 0) {
            sizeIdx = [0, 1, 2].find((i) => !used.has(i));
            used.add(sizeIdx);
          }
          if (finishIdx < 0) {
            finishIdx = [0, 1, 2].find((i) => !used.has(i));
          }
        } else if (n === 2) {
          if (boltIdx < 0 && sizeIdx < 0 && finishIdx < 0) {
            boltIdx = 0;
            sizeIdx = 1;
            finishIdx = -1;
          } else {
            const idxs = [0, 1];
            if (sizeIdx < 0) sizeIdx = idxs.find((i) => i !== boltIdx && i !== finishIdx) ?? 1;
            if (boltIdx < 0 && finishIdx >= 0) {
              boltIdx = idxs.find((i) => i !== finishIdx && i !== sizeIdx);
            }
            if (boltIdx < 0 && finishIdx < 0) {
              boltIdx = idxs.find((i) => i !== sizeIdx);
              if (boltIdx < 0) boltIdx = 0;
            }
            if (finishIdx < 0) finishIdx = -1;
          }
        }

        if (sizeIdx < 0) return null;

        return {
          boltIdx: boltIdx >= 0 ? boltIdx : -1,
          sizeIdx,
          finishIdx: finishIdx >= 0 ? finishIdx : -1,
          labels,
        };
      }

      static splitWheelCardLines(sizeValue) {
        const raw = String(sizeValue || '').trim();
        if (!raw) return { main: '', sub: '' };

        let main = raw;
        let sub = '';
        const offsetPattern = /\b(?:offset\s*[:+]?\s*)([+-]?\d+(?:\.\d+)?)\b/i;
        const m = main.match(offsetPattern);
        if (m) {
          let num = m[1];
          if (!/^[-+]/.test(num)) num = `+${num}`;
          sub = `Offset: ${num}`;
          main = main
            .replace(m[0], ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .replace(/[-–—]\s*$/, '')
            .trim();
        }
        return { main: main || raw, sub };
      }

      static getVariantOptionValue(variant, optionIndex) {
        if (!variant || optionIndex < 0) return '';
        return variant[`option${optionIndex + 1}`] || '';
      }

      static formatTireMoney(cents) {
        if (window.Shopify?.formatMoney) {
          return QuickAddModal.stripTrailingPriceZeros(Shopify.formatMoney(cents));
        }
        return `$${Math.round(cents / 100)}`;
      }

      static getVariantBasePriceCents(variants, selectedValues, addOnOptionIndex = -1) {
        const matching = (variants || []).filter((variant) =>
          (selectedValues || []).every((value, index) => {
            if (addOnOptionIndex >= 0 && index === addOnOptionIndex) return true;
            if (!value) return true;
            return QuickAddModal.getVariantOptionValue(variant, index) === value;
          })
        );
        if (!matching.length) return null;
        return Math.min(...matching.map((variant) => Number(variant.price) || 0));
      }

      static createPriceBreakdownCard({ totalLabel = 'Total:', optionLabels = [] } = {}) {
        const card = document.createElement('div');
        card.className = 'quick-add-price-breakdown quick-add-tire-total';

        const optionRows = (optionLabels || [])
          .map(
            (label, index) => `
            <div class="quick-add-tire-total__row quick-add-tire-total__row--option" data-option-index="${index}">
              <span class="quick-add-tire-total__label">${label || ''}</span>
              <span class="quick-add-tire-total__option-value"></span>
            </div>`
          )
          .join('');

        card.innerHTML = `
          <div class="quick-add-tire-total__row quick-add-tire-total__row--base">
            <span class="quick-add-tire-total__label">Base Price</span>
            <span class="quick-add-tire-total__base-price"></span>
          </div>
          ${optionRows}
          <div class="quick-add-tire-total__row quick-add-tire-total__row--grand">
            <span class="quick-add-tire-total__label">${totalLabel}</span>
            <span class="quick-add-tire-total__price"></span>
          </div>
        `;
        return card;
      }

      static updatePriceBreakdown(
        card,
        { baseCents, totalCents, optionLabels = [], selectedValues = [], optionDeltas = [] } = {}
      ) {
        if (!card) return;

        const baseEl = card.querySelector('.quick-add-tire-total__base-price');
        const totalEl = card.querySelector('.quick-add-tire-total__price');
        const safeBase = Number.isFinite(baseCents) ? baseCents : totalCents;
        const safeTotal = Number.isFinite(totalCents) ? totalCents : safeBase;

        if (baseEl) baseEl.textContent = QuickAddModal.formatTireMoney(safeBase);
        if (totalEl) totalEl.textContent = QuickAddModal.formatTireMoney(safeTotal);

        const labels = optionLabels.length
          ? optionLabels
          : Array.from(card.querySelectorAll('.quick-add-tire-total__row--option')).map(
              (row) => row.querySelector('.quick-add-tire-total__label')?.textContent || ''
            );

        labels.forEach((label, index) => {
          let row = card.querySelector(
            `.quick-add-tire-total__row--option[data-option-index="${index}"]`
          );
          if (!row) {
            row = document.createElement('div');
            row.className = 'quick-add-tire-total__row quick-add-tire-total__row--option';
            row.dataset.optionIndex = String(index);
            row.innerHTML =
              '<span class="quick-add-tire-total__label"></span><span class="quick-add-tire-total__option-value"></span>';
            const grand = card.querySelector('.quick-add-tire-total__row--grand');
            if (grand) card.insertBefore(row, grand);
            else card.appendChild(row);
          }

          const labelEl = row.querySelector('.quick-add-tire-total__label');
          const valueEl = row.querySelector('.quick-add-tire-total__option-value');
          const value = selectedValues[index] || '';
          const delta = Math.max(0, Number(optionDeltas[index]) || 0);

          if (labelEl) labelEl.textContent = label || '';
          if (valueEl) {
            valueEl.textContent =
              delta > 0
                ? `${value}  +${QuickAddModal.formatTireMoney(delta)}`.trim()
                : value;
          }
          row.hidden = !value;
        });
      }

      static TIRE_SELECT_CARET_HTML =
        '<svg class="icon icon-caret" viewBox="0 0 10 6" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/></svg>';

      static cloneQuickAddCartIcon() {
        const iconTemplate = document.querySelector('template.quick-add-cart-icon-source');
        const templateIcon = iconTemplate?.content?.querySelector('svg');
        if (templateIcon) {
          const svg = templateIcon.cloneNode(true);
          svg.style.color = '#ffffff';
          return svg;
        }

        const headerIcon = document.querySelector('#cart-icon-bubble .icon-cart');
        if (headerIcon) {
          const svg = headerIcon.cloneNode(true);
          svg.style.color = '#ffffff';
          return svg;
        }

        return null;
      }

      async ensureNativeVariantPicker(productElement) {
        const infoWrapper = productElement?.querySelector('.product__info-wrapper');
        const nativePicker = infoWrapper?.querySelector('variant-selects') ||
          this.modalContent?.querySelector('variant-selects');
        const initialSelections = this.getNativeVariantSelections(nativePicker);
        // Always strip Dawn's picker — custom modal UIs replace it, and leftover
        // options (incl. impossible/unavailable) must not stay visible.
        this.modalContent?.querySelectorAll('variant-selects').forEach((el) => el.remove());

        if (this.modalContent?.querySelector('.quick-add-tire-options')) return;

        const priceBox = infoWrapper?.querySelector('.quick-add-price-cta-box');
        if (!priceBox) return;
        if (priceBox.querySelector('.quick-add-standard-variant-options')) return;

        const productUrl = productElement.dataset.url || this.openedBy?.getAttribute('data-product-url');
        if (!productUrl) return;

        let product = this.quickAddProduct;
        if (!product || !product.variants?.length) {
          try {
            const response = await fetch(`${productUrl}.js`);
            if (!response.ok) return;
            product = await response.json();
            this.quickAddProduct = product;
          } catch (error) {
            return;
          }
        }

        if (!product?.variants?.length || product.variants.length <= 1) return;

        const optionLabels = QuickAddModal.normalizeProductOptionLabels(product.options);
        if (optionLabels.length === 1 && optionLabels[0] === 'Title') return;

        this.buildStandardVariantOptions(productElement, product, priceBox, initialSelections);
      }

      async ensurePriceBreakdown(productElement) {
        const infoWrapper = productElement?.querySelector('.product__info-wrapper');
        const priceBox = infoWrapper?.querySelector('.quick-add-price-cta-box');
        if (!priceBox) return;

        // Wheel / tire / standard pickers may already have installed a breakdown.
        const existing = priceBox.querySelector('.quick-add-price-breakdown, .quick-add-tire-total');
        if (existing) {
          priceBox.classList.add('quick-add-price-cta-box--breakdown');
          const priceBlock = priceBox.querySelector('[id^="price-quickadd-"]');
          if (priceBlock) priceBlock.hidden = true;
          return;
        }

        const productUrl = productElement.dataset.url || this.openedBy?.getAttribute('data-product-url');
        if (!productUrl) return;

        let product = this.quickAddProduct;
        if (!product || !product.variants?.length) {
          try {
            const response = await fetch(`${productUrl}.js`);
            if (!response.ok) return;
            product = await response.json();
            this.quickAddProduct = product;
          } catch (error) {
            return;
          }
        }

        const variants = (product.variants || []).filter(Boolean);
        if (!variants.length) return;

        const optionLabels = QuickAddModal.normalizeProductOptionLabels(product.options).filter(
          (label) => label && String(label).trim().toLowerCase() !== 'title'
        );
        const addOnOptionIndex = optionLabels.length >= 1 ? optionLabels.length - 1 : -1;

        const priceBlock = priceBox.querySelector('[id^="price-quickadd-"]');
        if (priceBlock) priceBlock.hidden = true;

        const breakdownCard = QuickAddModal.createPriceBreakdownCard({
          totalLabel: 'Total:',
          optionLabels,
        });
        // Keep option pickers (if any) above the breakdown.
        const picker = priceBox.querySelector(
          '.quick-add-standard-variant-options, .quick-add-variant-picker'
        );
        if (picker?.nextSibling) {
          priceBox.insertBefore(breakdownCard, picker.nextSibling);
        } else if (picker) {
          picker.insertAdjacentElement('afterend', breakdownCard);
        } else {
          priceBox.insertBefore(breakdownCard, priceBox.firstChild);
        }
        priceBox.classList.add('quick-add-price-cta-box--breakdown');

        const applyBreakdownForVariant = (variant) => {
          if (!variant) return;
          const selectedValues = optionLabels.map((_, optionIndex) =>
            QuickAddModal.getVariantOptionValue(variant, optionIndex)
          );
          const baseCents =
            QuickAddModal.getVariantBasePriceCents(variants, selectedValues, addOnOptionIndex) ??
            variant.price;
          const optionDeltas = optionLabels.map((_, index) =>
            index === addOnOptionIndex ? Math.max(0, variant.price - baseCents) : 0
          );
          QuickAddModal.updatePriceBreakdown(breakdownCard, {
            baseCents,
            totalCents: variant.price,
            optionLabels,
            selectedValues,
            optionDeltas,
          });
        };

        const variantInput = priceBox.querySelector('.product-variant-id, input[name="id"]');
        const currentId = variantInput?.value;
        const currentVariant =
          variants.find((variant) => String(variant.id) === String(currentId)) ||
          variants.find((variant) => variant.available) ||
          variants[0];
        applyBreakdownForVariant(currentVariant);

        // Keep breakdown in sync when the standard picker (or any form control) changes variant.
        const syncFromInput = () => {
          const id = variantInput?.value;
          const variant =
            variants.find((entry) => String(entry.id) === String(id)) || currentVariant;
          applyBreakdownForVariant(variant);
        };
        variantInput?.addEventListener('change', syncFromInput);
        priceBox.addEventListener('change', syncFromInput);
      }

      getNativeVariantSelections(nativePicker) {
        if (!nativePicker) return null;

        const values = [];
        nativePicker
          .querySelectorAll(':scope > fieldset, :scope > .product-form__input--dropdown')
          .forEach((block) => {
            const select = block.querySelector('select');
            if (select) {
              values.push(select.value);
              return;
            }

            const checked = block.querySelector('input[type="radio"]:checked');
            if (checked?.value) values.push(checked.value);
          });

        return values.length ? values : null;
      }

      static getProductOptionValues(options, optionIndex, variants) {
        const option = options?.[optionIndex];
        if (option && typeof option === 'object' && Array.isArray(option.values) && option.values.length) {
          return option.values.filter(Boolean);
        }
        return QuickAddModal.getUniqueOptionValues(variants, optionIndex);
      }

      static getVariantPickerOptionValue(root, optionIndex) {
        const select = root?.querySelector(`.quick-add-variant-option__select[data-option-index="${optionIndex}"]`);
        if (select) return select.value;

        const active = root?.querySelector(
          `.quick-add-variant-segmented[data-option-index="${optionIndex}"] .quick-add-variant-segmented__btn.is-selected`
        );
        return active?.dataset.value || '';
      }

      shouldUseSegmentedVariantOption(productElement, valueCount) {
        return !this.isWheelTireModalProduct(productElement) && valueCount === 2;
      }

      buildStandardVariantOptions(productElement, product, priceBox, initialSelections = null) {
        const form = priceBox.querySelector('form[data-type="add-to-cart-form"]');
        const formId = form?.id || '';
        const variants = (product.variants || []).filter(Boolean);
        const optionLabels = QuickAddModal.normalizeProductOptionLabels(product.options);
        // Last option can carry a premium over the cheapest matching combination.
        const addOnOptionIndex = optionLabels.length >= 1 ? optionLabels.length - 1 : -1;

        const root = document.createElement('div');
        root.className = 'quick-add-standard-variant-options quick-add-variant-picker';

        const priceBlock = priceBox.querySelector('[id^="price-quickadd-"]');
        if (priceBlock) priceBlock.hidden = true;

        let breakdownCard = priceBox.querySelector('.quick-add-price-breakdown');
        if (!breakdownCard) {
          breakdownCard = QuickAddModal.createPriceBreakdownCard({
            totalLabel: 'Total:',
            optionLabels,
          });
          priceBox.insertBefore(breakdownCard, priceBox.firstChild);
        }
        priceBox.classList.add('quick-add-price-cta-box--breakdown');

        const getSelectedValues = () =>
          optionLabels.map((_, optionIndex) =>
            QuickAddModal.getVariantPickerOptionValue(root, optionIndex)
          );

        const getCompatibleValues = (optionIndex, selectedValues) => {
          const matching = variants.filter((variant) =>
            selectedValues.every((value, index) => {
              if (index >= optionIndex || !value) return true;
              return QuickAddModal.getVariantOptionValue(variant, index) === value;
            })
          );
          // Filter by real combinations only — all matching options stay selectable.
          return QuickAddModal.getUniqueOptionValues(matching, optionIndex);
        };

        const syncOptionGroupVisibility = (optionIndex, values) => {
          const group = root.querySelector(`.quick-add-variant-option[data-option-index="${optionIndex}"]`);
          if (!group) return;
          group.hidden = values.length === 0;
        };

        const applyVariant = () => {
          const selectedValues = getSelectedValues();
          const variant =
            variants.find((entry) =>
              selectedValues.every((value, index) => QuickAddModal.getVariantOptionValue(entry, index) === value)
            ) || null;
          if (!variant) return;

          const variantInput = priceBox.querySelector('.product-variant-id');
          if (variantInput) {
            variantInput.value = variant.id;
            variantInput.disabled = !variant.available;
          }

          this.syncNativeVariantPicker(variant, product);
          this.updateVariantMedia(variant);
          this.updateAvailabilityBar(variant);

          const submit = priceBox.querySelector('.product-form__submit, button[name="add"]');
          if (submit) this.updateGuidePartSubmitButton(submit, variant);

          const baseCents =
            QuickAddModal.getVariantBasePriceCents(variants, selectedValues, addOnOptionIndex) ??
            variant.price;
          const optionDeltas = optionLabels.map((_, index) =>
            index === addOnOptionIndex ? Math.max(0, variant.price - baseCents) : 0
          );
          QuickAddModal.updatePriceBreakdown(breakdownCard, {
            baseCents,
            totalCents: variant.price,
            optionLabels,
            selectedValues,
            optionDeltas,
          });

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                variant,
                sectionId: productElement.dataset.section,
              },
            });
          }
        };

        const populateSelect = (select, values, preferredValue) => {
          QuickAddModal.fillSelectOptions(select, values, { preferredValue });
        };

        const populateSegmented = (segmented, values, preferredValue, onChange) => {
          const previous =
            preferredValue ||
            segmented.querySelector('.quick-add-variant-segmented__btn.is-selected')?.dataset.value ||
            '';
          const resolved = values.includes(previous) ? previous : values[0] || '';
          segmented.innerHTML = '';

          values.forEach((value) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'quick-add-variant-segmented__btn';
            button.dataset.value = value;
            button.textContent = value;
            button.classList.toggle('is-selected', value === resolved);
            button.addEventListener('click', () => {
              segmented.querySelectorAll('.quick-add-variant-segmented__btn').forEach((entry) => {
                entry.classList.toggle('is-selected', entry === button);
              });
              onChange();
            });
            segmented.appendChild(button);
          });
        };

        const refreshDependentOptions = (changedIndex = -1) => {
          const selectedValues = getSelectedValues();

          for (let optionIndex = Math.max(0, changedIndex + 1); optionIndex < optionLabels.length; optionIndex++) {
            const values = getCompatibleValues(optionIndex, selectedValues);
            const preferred =
              optionIndex > changedIndex
                ? selectedValues[optionIndex] || initialSelections?.[optionIndex]
                : selectedValues[optionIndex];

            const select = root.querySelector(
              `.quick-add-variant-option__select[data-option-index="${optionIndex}"]`
            );
            const segmented = root.querySelector(
              `.quick-add-variant-segmented[data-option-index="${optionIndex}"]`
            );

            if (select) {
              populateSelect(select, values, preferred);
            } else if (segmented) {
              populateSegmented(segmented, values, preferred, () => {
                refreshDependentOptions(optionIndex);
              });
            }

            syncOptionGroupVisibility(optionIndex, values);
            selectedValues[optionIndex] = QuickAddModal.getVariantPickerOptionValue(root, optionIndex);
          }

          applyVariant();
        };

        optionLabels.forEach((optionName, optionIndex) => {
          if (!optionName) return;

          const allValues = QuickAddModal.getProductOptionValues(product.options, optionIndex, variants);
          const group = document.createElement('div');
          group.className = 'product-form__input quick-add-variant-option';
          group.dataset.optionIndex = String(optionIndex);

          const label = document.createElement('div');
          label.className = 'form__label';
          label.textContent = optionName;
          group.appendChild(label);

          if (this.shouldUseSegmentedVariantOption(productElement, allValues.length)) {
            group.classList.add('quick-add-variant-option--segmented');

            const segmented = document.createElement('div');
            segmented.className = 'quick-add-variant-segmented';
            segmented.setAttribute('role', 'group');
            segmented.setAttribute('aria-label', optionName);
            segmented.dataset.optionIndex = String(optionIndex);
            group.appendChild(segmented);
          } else {
            group.classList.add('product-form__input--dropdown');

            const selectWrap = document.createElement('div');
            selectWrap.className = 'select';

            const select = document.createElement('select');
            select.className = 'select__select quick-add-variant-option__select';
            select.setAttribute('aria-label', optionName);
            if (formId) select.setAttribute('form', formId);
            select.dataset.optionIndex = String(optionIndex);

            const caret = document.createElement('span');
            caret.className = 'svg-wrapper quick-add-variant-option__caret';
            caret.setAttribute('aria-hidden', 'true');
            caret.innerHTML = QuickAddModal.TIRE_SELECT_CARET_HTML;

            selectWrap.appendChild(select);
            selectWrap.appendChild(caret);
            group.appendChild(selectWrap);

            select.addEventListener('change', () => {
              refreshDependentOptions(optionIndex);
            });
          }

          root.appendChild(group);
        });

        priceBox.insertBefore(root, priceBox.firstChild);

        // Seed first option from initial selection, then cascade compatible values downward.
        const firstValues = getCompatibleValues(0, []);
        const firstPreferred =
          initialSelections?.[0] && firstValues.includes(initialSelections[0])
            ? initialSelections[0]
            : firstValues[0];
        const firstSelect = root.querySelector('.quick-add-variant-option__select[data-option-index="0"]');
        const firstSegmented = root.querySelector('.quick-add-variant-segmented[data-option-index="0"]');
        if (firstSelect) {
          populateSelect(firstSelect, firstValues, firstPreferred);
        } else if (firstSegmented) {
          populateSegmented(firstSegmented, firstValues, firstPreferred, () => {
            refreshDependentOptions(0);
          });
        }
        syncOptionGroupVisibility(0, firstValues);

        refreshDependentOptions(0);
        this.setupSuspensionSpringRates(productElement, product, priceBox, root);
      }

      setupWheelQuickAdd(productElement) {
        const infoWrapper = productElement?.querySelector('.product__info-wrapper');
        const infoContainer = infoWrapper?.querySelector('.product__info-container');
        const priceBox = infoWrapper?.querySelector('.quick-add-price-cta-box');
        if (!infoContainer || !priceBox) return;

        if (!infoContainer.querySelector('.quick-add-tire-footnote')) {
          const footnote = document.createElement('p');
          footnote.className = 'quick-add-tire-footnote';
          footnote.textContent =
            'Price shown is per wheel. Set of 4 wheels required for most vehicles.';
          priceBox.insertAdjacentElement('afterend', footnote);
        }
      }

      async setupWheelOptions(productElement) {
        const infoWrapper = productElement?.querySelector('.product__info-wrapper');
        const infoContainer = infoWrapper?.querySelector('.product__info-container');
        const priceBox = infoWrapper?.querySelector('.quick-add-price-cta-box');
        if (!infoContainer || !priceBox || infoContainer.querySelector('.quick-add-tire-options')) return;

        const productUrl = productElement.dataset.url || this.openedBy?.getAttribute('data-product-url');
        if (!productUrl) {
          this.setupWheelQuickAdd(productElement);
          return;
        }

        let product;
        try {
          const response = await fetch(`${productUrl}.js`);
          if (!response.ok) {
            this.setupWheelQuickAdd(productElement);
            return;
          }
          product = await response.json();
          this.quickAddProduct = product;
        } catch (e) {
          this.setupWheelQuickAdd(productElement);
          return;
        }

        const variants = (product.variants || []).filter(Boolean);
        if (!variants.length) {
          this.setupWheelQuickAdd(productElement);
          return;
        }

        const indices = QuickAddModal.getWheelOptionIndices(product.options);
        if (!indices) {
          this.setupWheelQuickAdd(productElement);
          return;
        }

        const { boltIdx, sizeIdx, finishIdx, labels } = indices;

        const priceBlock = priceBox.querySelector('[id^="price-quickadd-"]');
        if (priceBlock) priceBlock.hidden = true;

        const optionsRoot = document.createElement('div');
        optionsRoot.className = 'quick-add-tire-options quick-add-wheel-options';

        let boltSelect = null;
        if (boltIdx >= 0) {
          const boltGroup = document.createElement('div');
          boltGroup.className = 'quick-add-tire-options__group quick-add-tire-options__group--diameter';
          const boltLab = document.createElement('label');
          boltLab.className = 'quick-add-tire-options__label';
          boltLab.textContent = labels[boltIdx] || '';
          const wrap = document.createElement('div');
          wrap.className = 'quick-add-tire-options__select';
          boltSelect = document.createElement('select');
          boltSelect.className = 'quick-add-tire-options__select-input';
          boltSelect.setAttribute('aria-label', labels[boltIdx] || '');
          const caret = document.createElement('span');
          caret.className = 'quick-add-tire-options__select-icon svg-wrapper';
          caret.setAttribute('aria-hidden', 'true');
          caret.innerHTML = QuickAddModal.TIRE_SELECT_CARET_HTML;
          wrap.appendChild(boltSelect);
          wrap.appendChild(caret);
          boltGroup.appendChild(boltLab);
          boltGroup.appendChild(wrap);
          optionsRoot.appendChild(boltGroup);

          QuickAddModal.fillSelectOptions(
            boltSelect,
            QuickAddModal.getUniqueOptionValues(variants, boltIdx)
          );
        }

        const sizeGroup = document.createElement('div');
        sizeGroup.className = 'quick-add-tire-options__group quick-add-tire-options__group--size';
        const sizeHeading = document.createElement('div');
        sizeHeading.className = 'quick-add-tire-options__label';
        sizeHeading.textContent = labels[sizeIdx] || '';
        const sizesList = document.createElement('div');
        sizesList.className = 'quick-add-tire-options__sizes';
        sizesList.setAttribute('role', 'listbox');
        sizesList.setAttribute('aria-label', labels[sizeIdx] || '');
        sizeGroup.appendChild(sizeHeading);
        sizeGroup.appendChild(sizesList);
        optionsRoot.appendChild(sizeGroup);

        let finishSelect = null;
        if (finishIdx >= 0) {
          const finishGroup = document.createElement('div');
          finishGroup.className = 'quick-add-tire-options__group quick-add-tire-options__group--diameter';
          const finishLab = document.createElement('label');
          finishLab.className = 'quick-add-tire-options__label';
          finishLab.textContent = labels[finishIdx] || '';
          const fWrap = document.createElement('div');
          fWrap.className = 'quick-add-tire-options__select';
          finishSelect = document.createElement('select');
          finishSelect.className = 'quick-add-tire-options__select-input';
          finishSelect.setAttribute('aria-label', labels[finishIdx] || '');
          const fCaret = document.createElement('span');
          fCaret.className = 'quick-add-tire-options__select-icon svg-wrapper';
          fCaret.setAttribute('aria-hidden', 'true');
          fCaret.innerHTML = QuickAddModal.TIRE_SELECT_CARET_HTML;
          fWrap.appendChild(finishSelect);
          fWrap.appendChild(fCaret);
          finishGroup.appendChild(finishLab);
          finishGroup.appendChild(fWrap);
          optionsRoot.appendChild(finishGroup);
        }

        const breakdownLabels = [];
        if (boltIdx >= 0) breakdownLabels.push(labels[boltIdx] || 'Bolt Pattern');
        breakdownLabels.push(labels[sizeIdx] || 'Size & Offset');
        if (finishIdx >= 0) breakdownLabels.push(labels[finishIdx] || 'Finish');

        const totalCard = QuickAddModal.createPriceBreakdownCard({
          totalLabel: 'Total (per wheel):',
          optionLabels: breakdownLabels,
        });
        const basePriceEl = totalCard.querySelector('.quick-add-tire-total__base-price');
        const totalPriceEl = totalCard.querySelector('.quick-add-tire-total__price');

        if (!infoContainer.querySelector('.quick-add-tire-footnote')) {
          const footnote = document.createElement('p');
          footnote.className = 'quick-add-tire-footnote';
          footnote.textContent =
            'Price shown is per wheel. Set of 4 wheels required for most vehicles.';
          priceBox.insertAdjacentElement('afterend', footnote);
        }

        infoContainer.insertBefore(optionsRoot, priceBox);
        priceBox.classList.add('quick-add-price-cta-box--tire');
        priceBox.insertBefore(totalCard, priceBox.firstChild);

        const matchesBolt = (v, boltVal) => {
          if (boltIdx < 0) return true;
          return QuickAddModal.getVariantOptionValue(v, boltIdx) === boltVal;
        };

        const matchesSize = (v, sizeVal) => QuickAddModal.getVariantOptionValue(v, sizeIdx) === sizeVal;

        const matchesFinish = (v, finishVal) => {
          if (finishIdx < 0) return true;
          if (!finishVal) return true;
          return QuickAddModal.getVariantOptionValue(v, finishIdx) === finishVal;
        };

        const pickVariant = (boltVal, sizeVal, finishVal) => {
          let list = variants.filter((v) => matchesBolt(v, boltVal) && matchesSize(v, sizeVal));
          if (finishIdx >= 0) list = list.filter((v) => matchesFinish(v, finishVal));
          const avail = list.find((v) => v.available);
          return avail || list[0] || null;
        };

        const applyVariant = (variant, boltVal, sizeVal) => {
          const b = boltVal !== undefined && boltVal !== null ? boltVal : boltSelect ? boltSelect.value : '';
          const s =
            sizeVal !== undefined && sizeVal !== null
              ? sizeVal
              : sizesList.querySelector('.quick-add-tire-size.is-selected')?.dataset.sizeValue;
          const forBase = variants.filter((v) => matchesBolt(v, b) && matchesSize(v, s));
          const baseCents =
            forBase.length > 0 && finishIdx >= 0
              ? Math.min(...forBase.map((v) => v.price))
              : variant.price;
          const finishVal = finishSelect?.value || '';
          const selectedValues = [];
          const optionDeltas = [];
          if (boltIdx >= 0) {
            selectedValues.push(b);
            optionDeltas.push(0);
          }
          selectedValues.push(s || '');
          optionDeltas.push(0);
          if (finishIdx >= 0) {
            selectedValues.push(finishVal);
            optionDeltas.push(Math.max(0, variant.price - baseCents));
          }

          this.selectTireVariant(
            variant,
            product,
            sizesList,
            totalPriceEl,
            basePriceEl,
            sizeIdx,
            null,
            null,
            null
          );

          QuickAddModal.updatePriceBreakdown(totalCard, {
            baseCents,
            totalCents: variant.price,
            optionLabels: breakdownLabels,
            selectedValues,
            optionDeltas,
          });
        };

        const renderFinishOptions = (boltVal, sizeVal) => {
          if (!finishSelect || finishIdx < 0) return;
          const list = variants.filter((v) => matchesBolt(v, boltVal) && matchesSize(v, sizeVal));
          const finishes = [];
          list.forEach((v) => {
            const f = QuickAddModal.getVariantOptionValue(v, finishIdx);
            if (f && !finishes.includes(f)) finishes.push(f);
          });

          QuickAddModal.fillSelectOptions(finishSelect, finishes, {
            preferredValue: finishSelect.value,
          });
        };

        const renderSizeCards = (boltVal) => {
          sizesList.innerHTML = '';
          const filtered = variants.filter((v) => matchesBolt(v, boltVal));
          const sizeValues = QuickAddModal.getUniqueOptionValues(filtered, sizeIdx);

          sizeValues.forEach((sizeVal) => {
            const candidates = filtered.filter((v) => matchesSize(v, sizeVal));
            const pool = candidates.filter((v) => v.available).length
              ? candidates.filter((v) => v.available)
              : candidates;
            if (!pool.length) return;

            const prices = pool.map((v) => v.price);
            const minPrice = Math.min(...prices);

            const { main, sub } = QuickAddModal.splitWheelCardLines(sizeVal);
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'quick-add-tire-size';
            btn.dataset.sizeValue = sizeVal;
            btn.setAttribute('role', 'option');
            btn.innerHTML =
              '<span class="quick-add-tire-size__text"><span class="quick-add-tire-size__label"></span><span class="quick-add-tire-size__meta"></span></span><span class="quick-add-tire-size__price"></span>';
            const labEl = btn.querySelector('.quick-add-tire-size__label');
            const metaEl = btn.querySelector('.quick-add-tire-size__meta');
            labEl.textContent = main;
            if (sub) {
              metaEl.textContent = sub;
              metaEl.hidden = false;
            } else {
              metaEl.textContent = '';
              metaEl.hidden = true;
            }
            btn.querySelector('.quick-add-tire-size__price').textContent = QuickAddModal.formatTireMoney(minPrice);
            btn.addEventListener('click', () => {
              renderFinishOptions(boltVal, sizeVal);
              const fv = finishSelect?.value || '';
              const variant = pickVariant(boltVal, sizeVal, fv);
              if (variant) applyVariant(variant, boltVal, sizeVal);
            });
            sizesList.appendChild(btn);
          });

          const firstSize = sizeValues[0];
          if (firstSize) {
            renderFinishOptions(boltVal, firstSize);
            const fv = finishSelect?.value || '';
            const firstVar = pickVariant(boltVal, firstSize, fv);
            if (firstVar) applyVariant(firstVar, boltVal, firstSize);
          }
        };

        const boltValInitial = boltSelect ? boltSelect.value : '';
        if (boltSelect) {
          boltSelect.addEventListener('change', () => renderSizeCards(boltSelect.value));
          renderSizeCards(boltValInitial);
        } else {
          renderSizeCards('');
        }

        if (finishSelect) {
          finishSelect.addEventListener('change', () => {
            const bVal = boltSelect ? boltSelect.value : '';
            const selectedBtn =
              sizesList.querySelector('.quick-add-tire-size.is-selected') ||
              sizesList.querySelector('.quick-add-tire-size');
            const sVal = selectedBtn?.dataset.sizeValue;
            if (!sVal) return;
            const variant = pickVariant(bVal, sVal, finishSelect.value);
            if (!variant) return;
            applyVariant(variant, bVal, sVal);
          });
        }

        console.log('[quick-add] wheel picker ready', {
          finishes: finishSelect?.options?.length ?? 0,
          variants: variants.length,
        });

        const submit = priceBox.querySelector('.product-form__submit, button[name="add"]');
        if (submit) {
          submit.querySelector('.quick-add-cart-icon')?.remove();
          delete submit.dataset.quickAddCartIcon;
        }
        QuickAddModal.enhanceAddToCartButton(priceBox);
      }

      async setupTireOptions(productElement) {
        const infoWrapper = productElement?.querySelector('.product__info-wrapper');
        const infoContainer = infoWrapper?.querySelector('.product__info-container');
        const priceBox = infoWrapper?.querySelector('.quick-add-price-cta-box');
        if (!infoContainer || !priceBox || infoContainer.querySelector('.quick-add-tire-options')) return;

        const productUrl = productElement.dataset.url || this.openedBy?.getAttribute('data-product-url');
        if (!productUrl) return;

        let product;
        try {
          const response = await fetch(`${productUrl}.js`);
          if (!response.ok) return;
          product = await response.json();
          this.quickAddProduct = product;
        } catch (error) {
          return;
        }

        const variants = (product.variants || []).filter(Boolean);
        if (!variants.length) return;

        const optionIndices = QuickAddModal.getTireOptionIndices(product.options);
        const { diameterOptionIndex, sizeOptionIndex } = optionIndices;
        if (sizeOptionIndex < 0) return;

        const priceBlock = priceBox.querySelector('[id^="price-quickadd-"]');
        if (priceBlock) priceBlock.hidden = true;

        const isWheel = this.isWheelProduct(productElement);

        const optionsRoot = document.createElement('div');
        optionsRoot.className = 'quick-add-tire-options';

        let diameterSelect = null;
        let diameterValues = [];

        if (diameterOptionIndex >= 0) {
          diameterValues = QuickAddModal.getUniqueOptionValues(variants, diameterOptionIndex);
          const diameterGroup = document.createElement('div');
          diameterGroup.className = 'quick-add-tire-options__group quick-add-tire-options__group--diameter';
          diameterGroup.innerHTML = `
            <label class="quick-add-tire-options__label">Select Diameter</label>
            <div class="quick-add-tire-options__select">
              <select class="quick-add-tire-options__select-input" aria-label="Select Diameter"></select>
              <span class="quick-add-tire-options__select-icon svg-wrapper" aria-hidden="true"></span>
            </div>
          `;
          diameterSelect = diameterGroup.querySelector('select');
          QuickAddModal.fillSelectOptions(diameterSelect, diameterValues);

          const caretTarget = diameterGroup.querySelector('.quick-add-tire-options__select-icon');
          if (caretTarget) caretTarget.innerHTML = QuickAddModal.TIRE_SELECT_CARET_HTML;

          optionsRoot.appendChild(diameterGroup);
        }

        const sizeGroup = document.createElement('div');
        sizeGroup.className = 'quick-add-tire-options__group quick-add-tire-options__group--size';
        const sizeHeading = isWheel ? 'Select Wheel Size' : 'Select Size';
        sizeGroup.innerHTML = `
          <div class="quick-add-tire-options__label">${sizeHeading}</div>
          <div class="quick-add-tire-options__sizes" role="listbox" aria-label="${sizeHeading}"></div>
        `;
        const sizesList = sizeGroup.querySelector('.quick-add-tire-options__sizes');
        optionsRoot.appendChild(sizeGroup);

        const tireOptionLabels = [];
        if (diameterOptionIndex >= 0) tireOptionLabels.push('Diameter');
        tireOptionLabels.push(isWheel ? 'Wheel Size' : 'Size');

        const totalLabel = isWheel ? 'Total (per wheel):' : 'Total (per tire):';
        const totalCard = QuickAddModal.createPriceBreakdownCard({
          totalLabel,
          optionLabels: tireOptionLabels,
        });
        const totalPriceEl = totalCard.querySelector('.quick-add-tire-total__price');

        const footnote = document.createElement('p');
        footnote.className = 'quick-add-tire-footnote';
        footnote.textContent = isWheel
          ? 'Price shown is per wheel. Set of 4 wheels recommended for most vehicles.'
          : 'Price shown is per tire. Set of 4 tires recommended for most vehicles.';

        infoContainer.insertBefore(optionsRoot, priceBox);
        priceBox.classList.add('quick-add-price-cta-box--tire');
        priceBox.insertBefore(totalCard, priceBox.firstChild);
        priceBox.insertAdjacentElement('afterend', footnote);

        const applyTireBreakdown = (variant, diameterValue) => {
          if (!variant) return;
          const selectedValues = [];
          if (diameterOptionIndex >= 0) {
            selectedValues.push(
              diameterValue || QuickAddModal.getVariantOptionValue(variant, diameterOptionIndex)
            );
          }
          selectedValues.push(
            QuickAddModal.getVariantOptionValue(variant, sizeOptionIndex) || variant.title || ''
          );
          QuickAddModal.updatePriceBreakdown(totalCard, {
            baseCents: variant.price,
            totalCents: variant.price,
            optionLabels: tireOptionLabels,
            selectedValues,
            optionDeltas: selectedValues.map(() => 0),
          });
        };

        const renderSizes = (diameterValue) => {
          sizesList.innerHTML = '';
          const filtered = variants.filter((variant) => {
            if (diameterOptionIndex < 0) return true;
            return QuickAddModal.getVariantOptionValue(variant, diameterOptionIndex) === diameterValue;
          });

          // Show every real size for this diameter — sold-out stays selectable.
          filtered.forEach((variant) => {
            const sizeLabel = QuickAddModal.getVariantOptionValue(variant, sizeOptionIndex) || variant.title;
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'quick-add-tire-size';
            btn.dataset.variantId = String(variant.id);
            btn.setAttribute('role', 'option');
            btn.innerHTML =
              '<span class="quick-add-tire-size__label"></span><span class="quick-add-tire-size__price"></span>';
            btn.querySelector('.quick-add-tire-size__label').textContent = sizeLabel;
            btn.querySelector('.quick-add-tire-size__price').textContent = QuickAddModal.formatTireMoney(
              variant.price
            );
            btn.addEventListener('click', () => {
              this.selectTireVariant(variant, product, sizesList, totalPriceEl);
              applyTireBreakdown(variant, diameterValue);
            });
            sizesList.appendChild(btn);
          });

          const first = filtered.find((variant) => variant.available) || filtered[0];
          if (first) {
            this.selectTireVariant(first, product, sizesList, totalPriceEl);
            applyTireBreakdown(first, diameterValue);
          }
        };

        if (diameterSelect) {
          diameterSelect.addEventListener('change', () => renderSizes(diameterSelect.value));
          renderSizes(diameterSelect.value);
        } else {
          renderSizes(null);
        }

        const submit = priceBox.querySelector('.product-form__submit, button[name="add"]');
        if (submit) {
          submit.querySelector('.quick-add-cart-icon')?.remove();
          delete submit.dataset.quickAddCartIcon;
        }
        QuickAddModal.enhanceAddToCartButton(priceBox);
      }

      static getUniqueOptionValues(variants, optionIndex) {
        const values = [];
        variants.forEach((variant) => {
          const value = QuickAddModal.getVariantOptionValue(variant, optionIndex);
          if (value && !values.includes(value)) values.push(value);
        });
        return values;
      }

      static fillSelectOptions(select, values, { preferredValue = '' } = {}) {
        if (!select) return;

        const previous = preferredValue || select.value;
        select.innerHTML = '';
        select.disabled = false;
        select.classList.remove('is-unavailable');

        values.forEach((value) => {
          const option = document.createElement('option');
          option.value = value;
          option.textContent = value;
          select.appendChild(option);
        });

        if (previous && values.includes(previous)) {
          select.value = previous;
        } else if (values[0]) {
          select.value = values[0];
        }
      }

      selectTireVariant(
        variant,
        product,
        sizesList,
        totalPriceEl,
        basePriceEl = null,
        sizeOptionIndexForCard = null,
        finishAdjustEl = null,
        wheelBaseCents = null,
        wheelFinishRowEl = null
      ) {
        if (!variant) return;

        console.log('[quick-add] selectTireVariant', {
          variantId: variant.id,
          variantTitle: variant.title || variant.name,
        });

        if (sizeOptionIndexForCard != null && sizeOptionIndexForCard >= 0 && sizesList) {
          const sv = QuickAddModal.getVariantOptionValue(variant, sizeOptionIndexForCard);
          sizesList.querySelectorAll('.quick-add-tire-size').forEach((btn) => {
            btn.classList.toggle('is-selected', btn.dataset.sizeValue === sv);
          });
        } else {
          sizesList?.querySelectorAll('.quick-add-tire-size').forEach((btn) => {
            btn.classList.toggle('is-selected', btn.dataset.variantId === String(variant.id));
          });
        }

        if (finishAdjustEl != null && wheelBaseCents != null) {
          if (basePriceEl) {
            basePriceEl.textContent = QuickAddModal.formatTireMoney(wheelBaseCents);
          }
          const delta = Math.max(0, variant.price - wheelBaseCents);
          if (wheelFinishRowEl) {
            wheelFinishRowEl.hidden = delta <= 0;
          }
          if (finishAdjustEl) {
            finishAdjustEl.textContent = delta > 0 ? `+${QuickAddModal.formatTireMoney(delta)}` : '';
          }
          if (totalPriceEl) {
            totalPriceEl.textContent = QuickAddModal.formatTireMoney(variant.price);
          }
        } else {
          if (totalPriceEl) {
            totalPriceEl.textContent = QuickAddModal.formatTireMoney(variant.price);
          }
          if (basePriceEl) {
            basePriceEl.textContent = QuickAddModal.formatTireMoney(variant.price);
          }
        }

        const variantInput = this.modalContent.querySelector('product-form input[name="id"]');
        if (variantInput) variantInput.value = variant.id;

        this.syncNativeVariantPicker(variant, product);
        this.updateVariantMedia(variant);

        const submit = this.modalContent.querySelector('.product-form__submit, button[name="add"]');
        if (submit) {
          this.updateGuidePartSubmitButton(submit, variant);
        }
      }

      syncNativeVariantPicker(variant, product) {
        const variantSelects = this.modalContent.querySelector('variant-selects');
        if (!variantSelects || !variant) return;

        const selectedVariantScript = variantSelects.querySelector('[data-selected-variant]');
        if (selectedVariantScript) selectedVariantScript.textContent = JSON.stringify(variant);

        const selects = variantSelects.querySelectorAll('select');
        selects.forEach((select, index) => {
          const value = variant[`option${index + 1}`];
          if (!value) return;

          select.value = value;
          Array.from(select.options).forEach((option) => {
            if (option.value === value) {
              option.setAttribute('selected', 'selected');
            } else {
              option.removeAttribute('selected');
            }
          });
        });

        const fieldsets = variantSelects.querySelectorAll('fieldset');
        fieldsets.forEach((fieldset, index) => {
          const value = variant[`option${index + 1}`];
          if (!value) return;

          const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
            (radio) => radio.value === value
          );
          if (input) input.checked = true;
        });
      }

      getThumbnailItemsForVariantMedia(productInfo) {
        if (this.quickAddThumbnailItems?.length) return this.quickAddThumbnailItems;
        if (!this.quickAddProduct) return [];
        return QuickAddModal.collectThumbnailItems(this.quickAddProduct, productInfo);
      }

      updateVariantSku(variant) {
        if (!variant || !this.modalContent) return;

        const productVariant =
          this.quickAddProduct?.variants?.find((entry) => Number(entry.id) === Number(variant.id)) ||
          variant;
        const sku = String(productVariant.sku || '').trim();

        this.modalContent.querySelectorAll('.product__sku, [id^="Sku-"]').forEach((skuEl) => {
          const label = skuEl.querySelector('.visually-hidden');
          const labelHtml = label ? label.outerHTML : '';
          skuEl.innerHTML = `${labelHtml}${sku}`;
          skuEl.classList.toggle('visibility-hidden', !sku);
        });

        const infoCard = this.modalContent.querySelector('.quick-add-product-info');
        if (!infoCard) return;

        let skuRow = infoCard.querySelector('.quick-add-product-info__row[data-info-key="sku"]');
        if (!sku) {
          skuRow?.remove();
          if (!infoCard.querySelector('.quick-add-product-info__row')) infoCard.remove();
          return;
        }

        if (!skuRow) {
          const list = infoCard.querySelector('.quick-add-product-info__list');
          if (!list) return;
          skuRow = document.createElement('div');
          skuRow.className = 'quick-add-product-info__row';
          skuRow.dataset.infoKey = 'sku';
          skuRow.innerHTML = '<dt>Part Number:</dt><dd></dd>';
          list.appendChild(skuRow);
        }

        const valueEl = skuRow.querySelector('dd');
        if (valueEl) valueEl.textContent = sku;
      }

      updateVariantMedia(variant, html = null) {
        if (!variant || !this.modalContent) {
          console.warn('[quick-add] updateVariantMedia skipped', {
            hasVariant: Boolean(variant),
            hasModal: Boolean(this.modalContent),
          });
          return;
        }

        this.updateVariantSku(variant);

        const productInfo = this.modalContent.querySelector('product-info');
        if (!productInfo) {
          console.warn('[quick-add] updateVariantMedia skipped — no product-info');
          return;
        }

        console.log('[quick-add] updateVariantMedia', {
          variantId: variant.id,
          variantTitle: variant.title || variant.name,
        });

        const usesCustomWheelPicker =
          this.classList.contains('quick-add--wheel') &&
          Boolean(this.modalContent.querySelector('.quick-add-wheel-options'));

        const originalSection = productInfo.dataset.originalSection;
        const sectionId = productInfo.dataset.section;

        if (
          html &&
          !usesCustomWheelPicker &&
          originalSection &&
          sectionId?.startsWith('quickadd-')
        ) {
          const sourceList = html.querySelector('media-gallery [id^="Slider-Gallery-"]');
          const destList = productInfo.querySelector('[id^="Slider-Gallery-"]');
          if (sourceList && destList) {
            let galleryHtml = sourceList.innerHTML;
            galleryHtml = galleryHtml.replaceAll(originalSection, sectionId);
            destList.innerHTML = galleryHtml;
          }
        }

        const productVariant =
          this.quickAddProduct?.variants?.find((v) => Number(v.id) === Number(variant.id)) || variant;

        const displayImage = QuickAddModal.resolveVariantDisplayImage(
          this.quickAddProduct,
          productVariant
        );

        console.log('[quick-add] selected variant image:', displayImage?.src ?? '(none)', {
          variantId: productVariant.id,
          variantTitle: productVariant.title || productVariant.name,
          isVariantSpecific: displayImage?.isVariantSpecific ?? false,
          hasProductJson: Boolean(this.quickAddProduct),
          variantFeaturedImage: productVariant.featured_image?.src || null,
          variantImageId: productVariant.image_id ?? null,
          assignedImageIds: Array.from(
            QuickAddModal.getVariantAssignedImageIds(this.quickAddProduct || {})
          ),
          productImageCount: QuickAddModal.getCatalogImages(this.quickAddProduct).length,
        });

        if (!displayImage) return;

        this.applyDisplayImageToGallery(productInfo, displayImage, productVariant);
      }

      fallbackUpdateActiveSlideImage(productInfo, variant) {
        const productVariant =
          this.quickAddProduct?.variants?.find((v) => Number(v.id) === Number(variant?.id)) || variant;
        const displayImage = QuickAddModal.resolveVariantDisplayImage(
          this.quickAddProduct,
          productVariant
        );
        if (!displayImage?.src || !productInfo) return;

        this.applyDisplayImageToGallery(productInfo, displayImage, productVariant);
      }

      async refreshThumbnailsStrip() {
        const productInfo = this.modalContent?.querySelector('product-info');
        await this.ensureThumbnailsStrip(productInfo);
      }

      syncQuickAddThumbnails(mediaId, variant = null, resolvedDisplayImage = null) {
        const strip = this.modalContent?.querySelector('.quick-add-thumbnails');
        if (!strip) return;

        const list = strip.querySelector('.quick-add-thumbnails__list');
        if (!list) return;

        const buttons = Array.from(list.querySelectorAll('.quick-add-thumbnails__item'));
        if (!buttons.length) return;

        const displayImage =
          resolvedDisplayImage ||
          QuickAddModal.resolveVariantDisplayImage(this.quickAddProduct, variant);
        const variantImageId = displayImage?.id ?? null;
        const variantSrcKey = displayImage?.src ? QuickAddModal.imageDedupKey(displayImage.src) : '';

        let activeBtn =
          (mediaId && buttons.find((btn) => btn.dataset.mediaId === mediaId)) ||
          (variantImageId &&
            buttons.find((btn) => btn.dataset.imageId && String(btn.dataset.imageId) === String(variantImageId))) ||
          (variantSrcKey &&
            buttons.find(
              (btn) => btn.dataset.imageSrc && QuickAddModal.imageDedupKey(btn.dataset.imageSrc) === variantSrcKey
            )) ||
          null;

        if (displayImage && !displayImage.isVariantSpecific) {
          const productDefault = QuickAddModal.getProductDefaultImage(this.quickAddProduct);
          activeBtn =
            (productDefault.id &&
              buttons.find(
                (btn) => btn.dataset.imageId && String(btn.dataset.imageId) === String(productDefault.id)
              )) ||
            buttons.find(
              (btn) =>
                btn.dataset.imageSrc &&
                QuickAddModal.imageDedupKey(btn.dataset.imageSrc) === productDefault.key
            ) ||
            buttons[0];
        }

        buttons.forEach((btn) => btn.classList.toggle('is-active', btn === activeBtn));
      }

      closeImageLightbox() {
        const lightbox = this.querySelector('.quick-add-image-lightbox');
        if (!lightbox) return;
        lightbox.hidden = true;
        lightbox.setAttribute('aria-hidden', 'true');
        const img = lightbox.querySelector('.quick-add-image-lightbox__img');
        if (img) {
          img.removeAttribute('src');
          img.alt = '';
        }
        this.lightboxImageIndex = -1;
      }

      getLightboxImages() {
        if (this.quickAddThumbnailItems?.length) {
          return this.quickAddThumbnailItems
            .map((item) => ({
              src: item.src,
              imageId: item.imageId || null,
              mediaId: item.mediaId || '',
              alt: '',
            }))
            .filter((item) => item.src);
        }

        const stripButtons = Array.from(
          this.modalContent?.querySelectorAll('.quick-add-thumbnails__item') || []
        );
        if (stripButtons.length) {
          return stripButtons
            .map((btn) => ({
              src: btn.dataset.imageSrc || btn.querySelector('img')?.src || '',
              imageId: btn.dataset.imageId || null,
              mediaId: btn.dataset.mediaId || '',
              alt: btn.getAttribute('aria-label') || '',
            }))
            .filter((item) => item.src);
        }

        const activeImg = this.modalContent?.querySelector('.product__media-item.is-active img');
        const src = activeImg?.currentSrc || activeImg?.src;
        return src ? [{ src, imageId: null, mediaId: '', alt: activeImg?.alt || '' }] : [];
      }

      setLightboxImage(index) {
        const lightbox = this.querySelector('.quick-add-image-lightbox');
        const images = this.lightboxImages || [];
        if (!lightbox || !images.length) return;

        const safeIndex = ((index % images.length) + images.length) % images.length;
        this.lightboxImageIndex = safeIndex;
        const item = images[safeIndex];

        const img = lightbox.querySelector('.quick-add-image-lightbox__img');
        if (img) {
          img.src = item.src;
          img.alt = item.alt || `Product image ${safeIndex + 1}`;
        }

        lightbox.querySelectorAll('.quick-add-image-lightbox__thumb').forEach((btn, btnIndex) => {
          const isActive = btnIndex === safeIndex;
          btn.classList.toggle('is-active', isActive);
          btn.setAttribute('aria-current', isActive ? 'true' : 'false');
          if (isActive) {
            btn.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
          }
        });

        // Keep the product modal gallery in sync when browsing lightbox thumbs.
        const productInfo = this.modalContent?.querySelector('product-info');
        if (productInfo && item.src) {
          this.applyDisplayImageToGallery(
            productInfo,
            { src: item.src, id: item.imageId, isVariantSpecific: Boolean(item.imageId) },
            null,
            { browse: true }
          );
          this.syncQuickAddThumbnails(item.mediaId || null, null, {
            src: item.src,
            id: item.imageId,
          });
        }
      }

      renderLightboxThumbnails(lightbox, images, activeIndex) {
        let strip = lightbox.querySelector('.quick-add-image-lightbox__thumbs');
        if (!strip) {
          strip = document.createElement('div');
          strip.className = 'quick-add-image-lightbox__thumbs';
          strip.setAttribute('role', 'listbox');
          strip.setAttribute('aria-label', 'Product images');
          lightbox.appendChild(strip);
        }

        strip.innerHTML = '';
        strip.hidden = images.length <= 1;

        images.forEach((item, index) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'quick-add-image-lightbox__thumb';
          btn.setAttribute('role', 'option');
          btn.setAttribute('aria-label', `Image ${index + 1}`);
          btn.classList.toggle('is-active', index === activeIndex);
          if (index === activeIndex) btn.setAttribute('aria-current', 'true');

          const thumb = document.createElement('img');
          thumb.loading = 'lazy';
          thumb.alt = '';
          thumb.src = QuickAddModal.thumbnailImageUrl
            ? QuickAddModal.thumbnailImageUrl(item.src)
            : item.src;
          btn.appendChild(thumb);

          btn.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();
            this.setLightboxImage(index);
          });

          strip.appendChild(btn);
        });
      }

      openImageLightbox(src, alt = '') {
        const images = this.getLightboxImages();
        if (!images.length && src) {
          images.push({ src, imageId: null, mediaId: '', alt });
        }
        if (!images.length) return;

        this.lightboxImages = images;

        let activeIndex = images.findIndex((item) => {
          if (!src) return false;
          return QuickAddModal.imageDedupKey(item.src) === QuickAddModal.imageDedupKey(src);
        });
        if (activeIndex < 0) activeIndex = 0;

        let lightbox = this.querySelector('.quick-add-image-lightbox');
        if (!lightbox) {
          lightbox = document.createElement('div');
          lightbox.className = 'quick-add-image-lightbox';
          lightbox.setAttribute('role', 'dialog');
          lightbox.setAttribute('aria-modal', 'true');
          lightbox.setAttribute('aria-label', 'Product image');
          lightbox.innerHTML = `
            <button type="button" class="quick-add-image-lightbox__close" data-lightbox-close aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
            <div class="quick-add-image-lightbox__stage">
              <button type="button" class="quick-add-image-lightbox__nav quick-add-image-lightbox__nav--prev" aria-label="Previous image">
                <span aria-hidden="true">‹</span>
              </button>
              <img class="quick-add-image-lightbox__img" alt="">
              <button type="button" class="quick-add-image-lightbox__nav quick-add-image-lightbox__nav--next" aria-label="Next image">
                <span aria-hidden="true">›</span>
              </button>
            </div>
          `;
          this.appendChild(lightbox);

          lightbox.addEventListener('click', (event) => {
            if (event.target === lightbox || event.target.closest('[data-lightbox-close]')) {
              this.closeImageLightbox();
              return;
            }
            if (event.target.closest('.quick-add-image-lightbox__nav--prev')) {
              event.preventDefault();
              event.stopPropagation();
              this.setLightboxImage((this.lightboxImageIndex || 0) - 1);
              return;
            }
            if (event.target.closest('.quick-add-image-lightbox__nav--next')) {
              event.preventDefault();
              event.stopPropagation();
              this.setLightboxImage((this.lightboxImageIndex || 0) + 1);
            }
          });
        }

        const navButtons = lightbox.querySelectorAll('.quick-add-image-lightbox__nav');
        navButtons.forEach((btn) => {
          btn.hidden = images.length <= 1;
        });

        this.renderLightboxThumbnails(lightbox, images, activeIndex);
        this.setLightboxImage(activeIndex);
        lightbox.hidden = false;
        lightbox.setAttribute('aria-hidden', 'false');
      }

      ensureImageLightbox(productElement) {
        const root = this.modalContent;
        if (!root || root.dataset.quickAddLightboxBound === 'true') return;
        root.dataset.quickAddLightboxBound = 'true';

        root.addEventListener(
          'click',
          (event) => {
            if (event.target.closest('.quick-add-thumbnails')) return;

            const mediaTarget = event.target.closest(
              '.product__modal-opener--image, .product__media-item.is-active .product__media, .product__media-item.is-active img'
            );
            if (!mediaTarget || !root.contains(mediaTarget)) return;

            const activeSlide =
              root.querySelector('.product__media-item.is-active') ||
              productElement?.querySelector('.product__media-item.is-active');
            const img = activeSlide?.querySelector('img') || mediaTarget.querySelector?.('img') || mediaTarget;
            const src = img?.currentSrc || img?.src;
            if (!src) return;

            event.preventDefault();
            event.stopPropagation();
            this.openImageLightbox(src, img?.alt || '');
          },
          true
        );

        if (!this._lightboxEscapeBound) {
          this._lightboxEscapeBound = true;
          document.addEventListener(
            'keyup',
            (event) => {
              if (!this.hasAttribute('open')) return;
              const lightbox = this.querySelector('.quick-add-image-lightbox:not([hidden])');
              if (!lightbox) return;

              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.closeImageLightbox();
                return;
              }

              if (event.key === 'ArrowLeft') {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.setLightboxImage((this.lightboxImageIndex || 0) - 1);
                return;
              }

              if (event.key === 'ArrowRight') {
                event.preventDefault();
                event.stopImmediatePropagation();
                this.setLightboxImage((this.lightboxImageIndex || 0) + 1);
              }
            },
            true
          );
        }
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      syncUrlWithProductId(productId) {
        const value = (productId || '').trim();
        if (!value) return;

        const nextHash = `#${encodeURIComponent(value)}`;
        if (location.hash === nextHash) return;

        history.pushState(null, '', `${location.pathname}${location.search}${nextHash}`);
      }

      clearUrlProductId() {
        const productId = (this.openedBy?.getAttribute('data-product-id') || '').trim();
        const hashProductId = QuickAddModal.getProductIdFromUrl();
        if (!location.hash) return;
        if (!productId || productId === hashProductId) {
          history.replaceState(null, '', `${location.pathname}${location.search}`);
        }
      }

      static getProductIdFromUrl() {
        const params = new URLSearchParams(window.location.search);
        const fromQuery = params.get('product_id') || params.get('product');
        let productId = fromQuery ? String(fromQuery).trim() : '';

        if (!productId) {
          const hash = (location.hash || '').replace(/^#/, '').trim();
          if (!hash) return '';

          try {
            productId = decodeURIComponent(hash).trim();
          } catch (error) {
            productId = hash.trim();
          }
        }

        if (productId.toLowerCase().startsWith('quickadd-')) {
          productId = productId.slice('quickadd-'.length);
        }

        return productId;
      }

      static findOpenerAndModal(productId) {
        const target = String(productId || '').trim();
        if (!target) return { opener: null, modal: null };

        const modalId = `QuickAdd-${target}`;
        const modal = document.getElementById(modalId);
        const modalSelector = `#${modalId}`;

        let opener = document.querySelector(`button.quick-add__submit[data-product-id="${target}"]`);

        if (!opener) {
          opener = document.querySelector(`button.guide-driver-part-card__details[data-product-id="${target}"]`);
        }

        if (!opener) {
          document.querySelectorAll('modal-opener').forEach((el) => {
            if (opener) return;
            const dataModal = el.getAttribute('data-modal') || '';
            if (dataModal === modalSelector) {
              opener = el.querySelector('button.quick-add__submit, button[data-product-id]');
            }
          });
        }

        return { opener, modal };
      }

      static async openFromHash(attempt = 0) {
        const productId = QuickAddModal.getProductIdFromUrl();
        if (!productId) {
          QuickAddModal._pendingDeepLinkId = null;
          return false;
        }

        if (QuickAddModal._pendingDeepLinkId === productId && attempt > 0) {
          const { modal } = QuickAddModal.findOpenerAndModal(productId);
          if (modal?.hasAttribute('open')) return true;
        }

        QuickAddModal._pendingDeepLinkId = productId;

        try {
          if (!customElements.get('modal-dialog')) {
            await customElements.whenDefined('modal-dialog');
          }
          if (!customElements.get('quick-add-modal')) {
            await customElements.whenDefined('quick-add-modal');
          }
        } catch (error) {
          return false;
        }

        const { opener, modal } = QuickAddModal.findOpenerAndModal(productId);
        if (!opener || !modal) {
          if (attempt < 25) {
            window.setTimeout(() => QuickAddModal.openFromHash(attempt + 1), 200);
          }
          return false;
        }

        const currentProductId = String(modal.openedBy?.getAttribute('data-product-id') || '').trim();
        if (modal.hasAttribute('open') && currentProductId === productId) {
          QuickAddModal._pendingDeepLinkId = null;
          return true;
        }

        if (modal.hasAttribute('open') && currentProductId !== productId) {
          modal.hide(true);
        }

        if (typeof modal.show === 'function') {
          modal.show(opener);
        } else {
          opener.click();
        }

        QuickAddModal._pendingDeepLinkId = null;
        return true;
      }

      static initUrlSync() {
        if (window.__quickAddUrlSyncInit) return;
        window.__quickAddUrlSyncInit = true;

        const run = () => QuickAddModal.openFromHash();

        window.addEventListener('hashchange', run);
        window.addEventListener('popstate', run);
        document.addEventListener('shopify:section:load', () => window.setTimeout(run, 150));

        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', run, { once: true });
        } else {
          run();
        }

        window.addEventListener('load', () => window.setTimeout(run, 100), { once: true });
      }
    }

  customElements.define('quick-add-modal', QuickAddModal);
  QuickAddModal.initUrlSync();
}

window.MuseQuickAddOpenFromUrl = () => {
  const ctor = customElements.get('quick-add-modal');
  if (ctor?.openFromHash) return ctor.openFromHash();
  return false;
};

console.log('[quick-add] script loaded');

